// src/pages/ClientDashboardPage.js
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  ListGroup,
  Badge,
  Modal,
  Form,
  CloseButton,
} from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import api from "../api";
import { AuthContext } from "../AuthContext";
import NavGuardModal from "../components/NavGuardModal";
import TransportationModal from "../components/TransportationModal";
import TimePicker from "../components/TimePicker";
import ProviderTypeahead from "../components/ProviderTypeahead";
import SalvationArmyBadge from "../components/SalvationArmyBadge";

function groupServiceTypesByCategory(serviceTypes) {
  const groups = {};
  (serviceTypes || []).forEach((st) => {
    const cat = st?.category || "Other";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(st);
  });

  // sort categories + each group
  const sortedCats = Object.keys(groups).sort((a, b) => a.localeCompare(b));
  const sortedGroups = {};
  for (const c of sortedCats) {
    sortedGroups[c] = groups[c].slice().sort((a, b) => {
      const al = (a?.label || a?.code || "").toLowerCase();
      const bl = (b?.label || b?.code || "").toLowerCase();
      return al.localeCompare(bl);
    });
  }
  return sortedGroups;
}

function toggleIdInArray(arr, id, checked) {
  const n = Number(id);
  const prev = Array.isArray(arr) ? arr.map(Number).filter(Number.isFinite) : [];
  if (checked) return Array.from(new Set([...prev, n]));
  return prev.filter((x) => x !== n);
}


function formatDate(d) {
  if (!d) return "";
  if (d instanceof Date) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return String(d).slice(0, 10);
}

// Render a UTC ISO timestamp in Clare County (Eastern) local time as
// "05/09/2026 8:46 AM".
function formatNoteTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d
    .toLocaleString("en-US", {
      timeZone: "America/Detroit",
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(",", "");
}

// Edit/delete allowed for ADMIN/DIRECTOR, INTAKE_WORKER, or the note's author.
function canEditNote(user, note) {
  if (!user) return false;
  const roles = (user.roles || []).map((r) => String(r).toUpperCase());
  if (roles.includes("ADMIN") || roles.includes("DIRECTOR") || roles.includes("INTAKE_WORKER")) {
    return true;
  }
  return note?.created_by != null && Number(note.created_by) === Number(user.id);
}

function formatName(c) {
  const parts = [c?.first_name, c?.middle_name, c?.last_name].filter(Boolean);
  return parts.join(" ");
}

function formatAddress(c) {
  const parts = [
    c?.address_line1,
    c?.address_line2,
    [c?.city, c?.state].filter(Boolean).join(", "),
    c?.postal_code,
  ].filter(Boolean);
  return parts.join(" ");
}

function pickPhone(c) {
  return c?.mobile_phone || c?.home_phone || "";
}

function calcAge(birthDate) {
  if (!birthDate) return "";
  const s = String(birthDate).slice(0, 10); // YYYY-MM-DD
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return "";

  const today = new Date();
  let age = today.getFullYear() - y;

  const hasHadBirthdayThisYear =
    today.getMonth() + 1 > m ||
    (today.getMonth() + 1 === m && today.getDate() >= d);

  if (!hasHadBirthdayThisYear) age -= 1;
  if (age < 0 || age > 130) return "";
  return String(age);
}

function getIntakeStatusBadge(intakeStatusRaw) {
  const s = String(intakeStatusRaw || "").trim().toLowerCase();
  const normalized =
    s === "partial intake"
      ? "partial"
      : s === "complete intake"
      ? "complete"
      : s === "completed"
      ? "complete"
      : s === "incomplete"
      ? "partial"
      : s;

  if (normalized === "partial") return { label: "Partial Intake", bg: "purple" };
  if (normalized === "complete") return { label: "Complete", bg: "success" };
  if (normalized === "open") return { label: "Open", bg: "warning", text: "dark" };

  if (s) return { label: String(intakeStatusRaw), bg: "secondary" };
  return null;
}

const SERVICE_STATUS_OPTIONS = [
  "Open",
  "Pending",
  "Denied",
  "Approved",
  "Complete",
  "Closed",
  "Request Cancelled",
];

// Transportation requests use one unified, ordered lifecycle. Staff may set only
// the decision/cancel states here; Confirmed/Assigned/Complete are reached on the
// Transportation Dashboard (and a driver is assigned only there).
const TRANSPORT_STATUS_FLOW = [
  "Open", "Approved", "Confirmed", "Assigned", "Complete", "Closed",
  "Denied", "Cancelled",
];
// Staff set only the decision/cancel states here; Confirmed/Assigned/Complete
// come from the Transportation Dashboard.
const TRANSPORT_STATUS_MANUAL = ["Open", "Approved", "Denied", "Cancelled"];

// Status dropdown options for a service. Transportation shows the unified set in
// order, offering the manual states plus whatever dashboard-driven value the
// request currently holds (so it stays visible and isn't lost on save).
function statusOptionsFor(isTransport, currentStatus) {
  if (!isTransport) return SERVICE_STATUS_OPTIONS;
  return TRANSPORT_STATUS_FLOW.filter(
    (s) => TRANSPORT_STATUS_MANUAL.includes(s) || s === currentStatus
  );
}

const RIDE_STATUS_META = {
  REQUESTED: { bg: "warning",   text: "dark",      label: "Requested"  },
  ASSIGNED:  { bg: "primary",   text: undefined,   label: "Assigned"   },
  CONFIRMED: { bg: "success",   text: undefined,   label: "Confirmed"  },
  COMPLETED: { bg: "secondary", text: undefined,   label: "Completed"  },
  CANCELLED: { bg: "danger",    text: undefined,   label: "Cancelled"  },
  NO_SHOW:   { bg: "dark",      text: undefined,   label: "No Show"    },
};

// Driver eligibility: who can drive whom
function driverIsEligible(driverType, riderCategory) {
  if (!riderCategory) return true;
  if (driverType === "COUPLE") return true;
  if (driverType === "MALE") return riderCategory === "MAN" || riderCategory === "COUPLE";
  if (driverType === "FEMALE") return riderCategory === "WOMAN" || riderCategory === "COUPLE";
  return false;
}

const BILL_PAY_AGENCIES = [
  { key: "bill_agency_dhhs",           label: "DHHS (SER)" },
  { key: "bill_agency_listening_ear",  label: "Listening Ear" },
  { key: "bill_agency_mmcaa",          label: "MMCAA" },
  { key: "bill_agency_salvation_army", label: "Salvation Army" },
  { key: "bill_agency_friend_family",  label: "Friend/Family" },
  { key: "bill_agency_church",         label: "Church" },
  { key: "bill_agency_other",          label: "Other" },
];

const BILL_PAY_DEFAULT_TYPES = [
  "Electric", "Natural Gas", "Water/Sewer", "Rent", "Mortgage",
  "Phone", "Internet", "Medical Bill", "Prescription", "Car Payment", "Other",
];

function calcBillPay(form) {
  const due = parseFloat(form.bill_amount_due) || 0;
  const clientHas = parseFloat(form.bill_client_has) || 0;
  const agencyTotal = BILL_PAY_AGENCIES.reduce((s, a) => s + (parseFloat(form[a.key]) || 0), 0);
  const stillNeeded = due - clientHas - agencyTotal;
  const clientOwes = stillNeeded - (parseFloat(form.bill_hhh_amount) || 0);
  return { due, clientHas, agencyTotal, stillNeeded, clientOwes };
}

const HELP_ALREADY_RECEIVED_OPTIONS = [
  "DHHS",
  "MMCAA",
  "Veterans",
  "Salvation Army",
  "Listening Ear",
  "Other",
];

const BED_COMPONENTS = [
  { label: "Frame",      twinField: "bed_twin_frames",      fullField: "bed_full_frames" },
  { label: "Box Spring", twinField: "bed_twin_box_springs", fullField: "bed_full_box_springs" },
  { label: "Mattress",   twinField: "bed_twin_mattresses",  fullField: "bed_full_mattresses" },
];

// Quantity is required IF any selected service type name is in this set (case-insensitive).
const QUANTITY_REQUIRED_TYPE_NAMES = new Set(
  ["beds", "laundry card", "food card", "furniture", "propane", "gas/gas card"].map((x) =>
    x.toLowerCase()
  )
);

function getCurrentUserId() {
  // Auth is stored under "hhh_auth" as { user, token } (see AuthContext).
  try {
    const raw = localStorage.getItem("hhh_auth");
    if (raw) {
      const id = JSON.parse(raw)?.user?.id;
      if (id != null) return Number(id);
    }
  } catch (_) {}
  return null;
}

export default function ClientDashboardPage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Capture on mount — navigate({ state: { openServiceId } }) from admin dashboard
  const autoOpenServiceId = useRef(location.state?.openServiceId ?? null);

  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [lastHelpedDate, setLastHelpedDate] = useState(null);
  const [familyMembers, setFamilyMembers] = useState([]);

  const [serviceTypes, setServiceTypes] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [printingPacket, setPrintingPacket] = useState(false);

  // Modals
  const [showAddService, setShowAddService] = useState(false);
  const [showEditService, setShowEditService] = useState(false);
  const [savingService, setSavingService] = useState(false);
  const [addServiceQueueTotal, setAddServiceQueueTotal] = useState(0);
  const [serviceFormError, setServiceFormError] = useState("");
  const [showHelpSection, setShowHelpSection] = useState(false);
  const [showFamilySection, setShowFamilySection] = useState(false);

  const [editingServiceId, setEditingServiceId] = useState(null);

  // Internal notes shown in the Edit Service modal (one-to-many, timestamped).
  const [editServiceNotes, setEditServiceNotes] = useState([]);
  const [addingNote, setAddingNote] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState("");
  const [noteError, setNoteError] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);

  const emptyServiceForm = useMemo(
    () => ({
      service_type_ids: [],
      service_date: formatDate(new Date()),
      quantity: 1,
      status: "Open",
      assigned_to: "",

      // NEW fields
      notes: "",
      internal_notes: "",
      pickup_delivery_date: "",
      tickets_requested: "",
      bill_type: "",
      bill_provider: "",
      bill_account_number: "",
      bill_amount_due: "",
      bill_client_has: "",
      bill_agency_dhhs: "",
      bill_agency_listening_ear: "",
      bill_agency_mmcaa: "",
      bill_agency_salvation_army: "",
      bill_agency_friend_family: "",
      bill_agency_church: "",
      bill_agency_other: "",
      bill_hhh_amount: "",
      bed_twin_frames: 0,
      bed_twin_box_springs: 0,
      bed_twin_mattresses: 0,
      bed_full_frames: 0,
      bed_full_box_springs: 0,
      bed_full_mattresses: 0,
      bed_delivery_method: "",
      service_location: "",
      laundry_type: "",
      laundry_needs_transport: false,
      food_type: "",
      food_card_value: "",
      food_card_qty: "",
      gas_type: "",
      gas_amount: "",
      gas_location_id: "",
      gas_appointment_reason: "",
      gas_appointment_verified: false,
      gas_appt_date: "",
      gas_destination: "",
      propane_type: "",
      propane_gallons: "",
      propane_fill_price: "",
      propane_location_id: "",
      propane_tank_service: "",
      propane_tank_count: "",
      propane_cost_per: "",
      propane_tank_count_100: "",
      propane_cost_per_100: "",
      help_already_received: [],
      family_helped: "No", // "Yes" | "No"
      family_help_offered: "",

      // Transportation fields (visible when transport service type selected)
      transport_ride_id: null,
      transport_pickup_date: "",
      transport_pickup_time: "08:00",
      transport_dropoff_location_text: "",
      transport_pickup_location_text: "",
      transport_provider_id: "",
      transport_rider_category: "",
      transport_driver_id: "",
      transport_appointment_confirmed: false,
      transport_driver_confirmed: false,
      transport_status: "REQUESTED",
    }),
    []
  );

  const [addServiceForm, setAddServiceForm] = useState(emptyServiceForm);
  const [editServiceForm, setEditServiceForm] = useState(emptyServiceForm);

  // Emergency-contact prompt: a transportation/pickup service requires the
  // client to have an emergency contact on file. If missing, prompt for it
  // (required) before the service is saved, then resume the save.
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyForm, setEmergencyForm] = useState({ name: "", phone: "" });
  const [emergencySaving, setEmergencySaving] = useState(false);
  const [emergencyError, setEmergencyError] = useState("");
  const emergencyProvidedRef = useRef(false);
  // True once the user chose "Skip for now" in the current add/edit session, so
  // the save can complete without re-prompting. Reset whenever the Add or Edit
  // Service modal opens, so a client with no contact is prompted again next time.
  const emergencySkippedRef = useRef(false);
  // Which save flow opened the prompt and should resume on Save/Skip:
  // "add" | "edit" | null (null = opened by checking the Transportation box,
  // so just record the contact and let the user keep filling out the form).
  const emergencyResumeRef = useRef(null);

  // Transportation (rides)
  const { user } = useContext(AuthContext);
  const [rides, setRides] = useState([]);
  const [showTransportModal, setShowTransportModal] = useState(false);
  const [editingRide, setEditingRide] = useState(null);

  // Transport lookup data for inline fields in Add Service modal
  const [allActiveTransportDrivers, setAllActiveTransportDrivers] = useState([]);
  const [transportDrivers, setTransportDrivers] = useState([]);
  const [transportProviders, setTransportProviders] = useState([]);
  const [transportLookupLoading, setTransportLookupLoading] = useState(false);

  const [lpLocations, setLpLocations] = useState([]);
  const [showAddLpLocation, setShowAddLpLocation] = useState(false);
  const [newLpLocationName, setNewLpLocationName] = useState("");
  const [savingLpLocation, setSavingLpLocation] = useState(false);
  const [gasLocations, setGasLocations] = useState([]);
  const [showAddGasLocation, setShowAddGasLocation] = useState(false);
  const [newGasLocationName, setNewGasLocationName] = useState("");
  const [savingGasLocation, setSavingGasLocation] = useState(false);
  const [showAddTransportProvider, setShowAddTransportProvider] = useState(false);
  const [newTransportProvider, setNewTransportProvider] = useState({ name: "", phone: "", city: "" });
  const [addingTransportProvider, setAddingTransportProvider] = useState(false);
  const [transportProviderError, setTransportProviderError] = useState("");

  // Task modal
  const [showTaskModal, setShowTaskModal]   = useState(false);
  const [taskServiceId, setTaskServiceId]   = useState(null);
  const [taskForm, setTaskForm]             = useState({ assigned_to: "", task: "" });
  const [taskSaving, setTaskSaving]         = useState(false);
  const [taskError, setTaskError]           = useState("");
  const [intakeWorkers, setIntakeWorkers]   = useState([]);

  const serviceTypeById = useMemo(() => {
    const m = new Map();
    for (const st of serviceTypes || []) m.set(Number(st.id), st);
    return m;
  }, [serviceTypes]);

  const userLabelById = useMemo(() => {
    const m = new Map();
    for (const u of users || []) {
      m.set(Number(u.id), u?.full_name || u?.email || `User #${u.id}`);
    }
    return m;
  }, [users]);

  // Only Intake Workers and Directors are assignable for service requests.
  const ASSIGNABLE_ROLES = ["INTAKE_WORKER", "DIRECTOR"];
  const assignableUsers = useMemo(() => {
    return (users || []).filter((u) => {
      const roles = (u?.roles || []).map((r) => String(r).toUpperCase());
      return roles.some((r) => ASSIGNABLE_ROLES.includes(r));
    });
  }, [users]);

  const normalizeTypeIdsFromService = (s) => {
    if (Array.isArray(s?.service_type_ids)) {
      return s.service_type_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x));
    }
    if (s?.service_type_id != null && s.service_type_id !== "") {
      const n = Number(s.service_type_id);
      return Number.isFinite(n) ? [n] : [];
    }
    return [];
  };

  const normalizeHelpAlready = (s) => {
    // prefer array
    if (Array.isArray(s?.help_already_received)) {
      return s.help_already_received.map(String).filter(Boolean);
    }
    // sometimes stored as CSV
    if (typeof s?.help_already_received === "string") {
      return s.help_already_received
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
    if (typeof s?.help_already_received_csv === "string") {
      return s.help_already_received_csv
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
    }
    return [];
  };

  const quantityRequiredForTypeIds = (typeIds) => {
    if (!typeIds || typeIds.length === 0) return false;
    for (const tid of typeIds) {
      const st = serviceTypeById.get(Number(tid));
      const nm = String(st?.name || "").trim().toLowerCase();
      if (nm && QUANTITY_REQUIRED_TYPE_NAMES.has(nm)) return true;
    }
    return false;
  };

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get(`/clients/${id}/dashboard`);
      setClient(res.data.client);
      setServices(res.data.services || []);
      setLastHelpedDate(res.data.last_helped_date || null);
      setFamilyMembers(res.data.family_members || []);
    } catch (e) {
      setError("Failed to load client dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadLookupsBestEffort = async () => {
    // do NOT break dashboard if these do not exist yet
    const tryEndpoints = async (urls) => {
      let lastErr = null;
      for (const u of urls) {
        try {
          const r = await api.get(u);
          return r;
        } catch (e) {
          lastErr = e;
        }
      }
      return null;
    };

    const [stRes, uRes] = await Promise.all([
      tryEndpoints(["clients/serviceTypes", "clients/service_types", "clients/service-types"]),
      tryEndpoints(["clients/users", "/app_users", "/user"]),
    ]);

    if (stRes) {
      const stData = stRes.data;
      setServiceTypes(stData?.items || stData?.service_types || stData || []);
    } else {
      setServiceTypes([]);
    }

    if (uRes) {
      const uData = uRes.data;
      setUsers(uData?.items || uData?.users || uData || []);
    } else {
      setUsers([]);
    }
  };

  const loadRides = async () => {
    try {
      const res = await api.get("/drivers/rides", { params: { client_id: id } });
      setRides(res.data.items || []);
    } catch (_) {
      // non-critical — silently ignore
    }
  };

  const handlePrintPacket = async () => {
    setPrintingPacket(true);
    try {
      const res = await api.get(`/clients/${id}/intake-packet.pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      // Open in a new tab so the user can review and print (Ctrl/Cmd+P).
      const win = window.open(url, "_blank");
      if (!win) {
        // Pop-up blocked — fall back to a download.
        const a = document.createElement("a");
        a.href = url;
        a.download = `Intake-Packet-${headerName || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Release the object URL after the tab has had time to load it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      setError("Could not generate the intake packet. Please try again.");
    } finally {
      setPrintingPacket(false);
    }
  };

  useEffect(() => {
    if (user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR")) {
      api.get("/tasks/intake-workers")
        .then((res) => setIntakeWorkers(res.data.items || []))
        .catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    loadDashboard();
    loadLookupsBestEffort();
    loadRides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Auto-open edit modal when arriving from admin dashboard with a specific service ID
  useEffect(() => {
    const targetId = autoOpenServiceId.current;
    if (!targetId || services.length === 0 || serviceTypeById.size === 0) return;
    const svc = services.find((s) => s.id === Number(targetId));
    if (svc) {
      autoOpenServiceId.current = null; // prevent re-trigger
      openEditService(svc);
      // Remove from browser history state so refresh doesn't re-open
      window.history.replaceState(null, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services, serviceTypeById]);

  const headerName = useMemo(() => formatName(client), [client]);

  const intakeDate = client?.intake_date ? formatDate(client.intake_date) : "";
  const lastHelped = lastHelpedDate ? formatDate(lastHelpedDate) : "";
  const phone = pickPhone(client);
  const address = formatAddress(client);

  // Single-line client identity shown at the top of the Add/Edit Service modals.
  const clientInfoLine = (
    <div className="small text-muted d-flex flex-wrap align-items-center gap-3 mb-2">
      <span className="fw-semibold text-body">{headerName || "—"}</span>
      {phone ? <span><i className="bi bi-telephone me-1" />{phone}</span> : null}
      {address ? <span><i className="bi bi-geo-alt me-1" />{address}</span> : null}
    </div>
  );

  const showLives = !!client?.lives_in_clare;
  const showChurch = !!client?.attends_church_in_clare;
  const showKids = !!client?.children_school_in_clare;
  const showVerify = !!client?.permission_to_verify;

  const functionalLimitations = (client?.functional_limitations || "").trim();
  const safetyAlerts = (client?.safety_alerts || "").trim();
  const specialCircumstances = (client?.special_circumstances || "").trim();

  const showFunctionalLimitations = functionalLimitations.length > 0;
  const showSafetyAlert = safetyAlerts.length > 0;
  const showSpecialCircumstances = specialCircumstances.length > 0;

  const intakeStatusBadge = getIntakeStatusBadge(client?.intake_status);

  const navStatus = client?.nav_status;
  const navRecommended = client?.nav_program_recommended;
  const navBadge = (() => {
    if (navStatus === "approved" || navStatus === "active") {
      return { label: "Navigation", bg: "success", text: null };
    }
    if (navRecommended && navStatus !== "denied") {
      return { label: "Referred to Navigation", bg: "warning", text: "dark" };
    }
    return null;
  })();

  // For Add: only inspect the first (current) type in the queue so each step shows one type's fields
  const addServiceIsTransport = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.category || "").toLowerCase().includes("transport");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  // For Edit: inspect all selected types (edit is always single-type)
  const editServiceIsTransport = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.category || "").toLowerCase().includes("transport");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  // Bus Card #-of-cards, Food type, and Laundry type are admin/director-only
  // inputs. Other staff can request the service but those details are set by an
  // admin/director (typically at approval); afterward they're shown read-only.
  const isAdminOrDirector = useMemo(
    () => !!(user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR")),
    [user]
  );
  // A request that has been approved (approved_by is stamped on approval).
  const editIsApproved = !!editServiceForm.approved_by;

  const readonlyRow = (label, value) => (
    <Form.Group className="mb-3">
      <Form.Label className="mb-0 text-muted" style={{ fontSize: "0.85rem" }}>{label}</Form.Label>
      <div className="fw-semibold">{value || "—"}</div>
    </Form.Group>
  );

  const adminOnlyDetailsNote = (
    <p className="text-muted fst-italic mb-0" style={{ fontSize: "0.9rem" }}>
      An administrator will complete these details when the request is approved.
    </p>
  );

  const addServiceIsBusCard = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("bus card");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsBusCard = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("bus card");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsBeds = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("bed");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsBeds = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("bed");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceNeedsDelivery = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    const name = (st?.name || "").toLowerCase();
    return name.includes("bed") || name.includes("furniture") || name.includes("kitchen");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceNeedsDelivery = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        const name = (st?.name || "").toLowerCase();
        return name.includes("bed") || name.includes("furniture") || name.includes("kitchen");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsPersonalBag = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    const combined = ((st?.name || "") + " " + (st?.label || "") + " " + (st?.category || "")).toLowerCase();
    return combined.includes("personal") && combined.includes("bag");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsPersonalBag = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        const combined = ((st?.name || "") + " " + (st?.label || "") + " " + (st?.category || "")).toLowerCase();
        return combined.includes("personal") && combined.includes("bag");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsBillPay = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    const combined = ((st?.name || "") + " " + (st?.label || "") + " " + (st?.category || "")).toLowerCase();
    return combined.includes("help pay a bill") || combined.includes("bill pay") || combined.includes("bill_pay");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsBillPay = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        const combined = ((st?.name || "") + " " + (st?.label || "") + " " + (st?.category || "")).toLowerCase();
        return combined.includes("help pay a bill") || combined.includes("bill pay") || combined.includes("bill_pay");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsLaundry = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("laundry");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsLaundry = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("laundry");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsLaundryMinistry = useMemo(
    () => addServiceIsLaundry && addServiceForm.laundry_type === "Ministry",
    [addServiceIsLaundry, addServiceForm.laundry_type]
  );

  const editServiceIsLaundryMinistry = useMemo(
    () => editServiceIsLaundry && editServiceForm.laundry_type === "Ministry",
    [editServiceIsLaundry, editServiceForm.laundry_type]
  );

  const addServiceIsGas = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("gas");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsGas = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("gas");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsPropane = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("propane");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsPropane = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("propane");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsFood = useMemo(() => {
    const st = serviceTypeById.get(Number(addServiceForm.service_type_ids[0]));
    return (st?.name || "").toLowerCase().includes("food");
  }, [addServiceForm.service_type_ids, serviceTypeById]);

  const editServiceIsFood = useMemo(
    () =>
      (editServiceForm.service_type_ids || []).some((tid) => {
        const st = serviceTypeById.get(Number(tid));
        return (st?.name || "").toLowerCase().includes("food");
      }),
    [editServiceForm.service_type_ids, serviceTypeById]
  );

  const addServiceIsFoodCard = useMemo(
    () => addServiceIsFood && addServiceForm.food_type === "Card",
    [addServiceIsFood, addServiceForm.food_type]
  );

  const editServiceIsFoodCard = useMemo(
    () => editServiceIsFood && editServiceForm.food_type === "Card",
    [editServiceIsFood, editServiceForm.food_type]
  );

  // True when transport fields should render (regular transport OR laundry ministry w/ transport)
  const addShowsTransport = useMemo(
    () => (addServiceIsTransport && !addServiceIsBusCard) ||
      (addServiceIsLaundryMinistry && !!addServiceForm.laundry_needs_transport),
    [addServiceIsTransport, addServiceIsBusCard, addServiceIsLaundryMinistry, addServiceForm.laundry_needs_transport]
  );

  const editShowsTransport = useMemo(
    () => (editServiceIsTransport && !editServiceIsBusCard) ||
      (editServiceIsLaundryMinistry && !!editServiceForm.laundry_needs_transport),
    [editServiceIsTransport, editServiceIsBusCard, editServiceIsLaundryMinistry, editServiceForm.laundry_needs_transport]
  );

  // Reactively filter/fetch eligible drivers when rider category or date/time changes
  const _updateEligibleDrivers = (riderCategory, pickupDate, pickupTime, excludeRideId = null) => {
    if (!riderCategory) {
      setTransportDrivers(allActiveTransportDrivers);
      return () => {};
    }
    if (!pickupDate || !pickupTime) {
      setTransportDrivers(
        allActiveTransportDrivers.filter((d) => driverIsEligible(d.driver_type, riderCategory))
      );
      return () => {};
    }
    let active = true;
    const params = {
      rider_category: riderCategory,
      pickup_datetime: `${pickupDate}T${pickupTime}:00`,
    };
    if (excludeRideId) params.exclude_ride_id = excludeRideId;
    api
      .get("/drivers/eligible", { params })
      .then((res) => {
        if (active) setTransportDrivers(res.data.items || []);
      })
      .catch(() => {
        if (active)
          setTransportDrivers(
            allActiveTransportDrivers.filter((d) => driverIsEligible(d.driver_type, riderCategory))
          );
      });
    return () => { active = false; };
  };

  useEffect(() => {
    if (!showAddService || !addShowsTransport) return;
    const { transport_rider_category, transport_pickup_date, transport_pickup_time } = addServiceForm;
    return _updateEligibleDrivers(transport_rider_category, transport_pickup_date, transport_pickup_time);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addServiceForm.transport_rider_category,
    addServiceForm.transport_pickup_date,
    addServiceForm.transport_pickup_time,
    showAddService,
    addShowsTransport,
    allActiveTransportDrivers,
  ]);

  useEffect(() => {
    if (!showEditService || !editShowsTransport) return;
    const { transport_rider_category, transport_pickup_date, transport_pickup_time, transport_ride_id } = editServiceForm;
    return _updateEligibleDrivers(transport_rider_category, transport_pickup_date, transport_pickup_time, transport_ride_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editServiceForm.transport_rider_category,
    editServiceForm.transport_pickup_date,
    editServiceForm.transport_pickup_time,
    editServiceForm.transport_ride_id,
    showEditService,
    editShowsTransport,
    allActiveTransportDrivers,
  ]);

  // Card is disabled for now, so default a gas request to Cash once the Gas
  // section is shown and no type has been chosen yet.
  useEffect(() => {
    if (addServiceIsGas && !addServiceForm.gas_type) {
      setAddServiceForm((p) => ({ ...p, gas_type: "Cash" }));
    }
  }, [addServiceIsGas, addServiceForm.gas_type]);
  useEffect(() => {
    if (editServiceIsGas && !editServiceForm.gas_type) {
      setEditServiceForm((p) => ({ ...p, gas_type: "Cash" }));
    }
  }, [editServiceIsGas, editServiceForm.gas_type]);

  const addServiceIsPropaneTank = addServiceIsPropane && addServiceForm.propane_type === "Tank";
  const editServiceIsPropaneTank = editServiceIsPropane && editServiceForm.propane_type === "Tank";
  useEffect(() => {
    if ((!addServiceIsPropaneTank && !editServiceIsPropaneTank) || lpLocations.length > 0) return;
    api.get("/clients/lp-locations").then((res) => setLpLocations(res.data.items || [])).catch(() => {});
  }, [addServiceIsPropaneTank, editServiceIsPropaneTank, lpLocations.length]);

  const handleSaveLpLocation = async (setForm, type = "") => {
    const name = newLpLocationName.trim();
    if (!name) return;
    setSavingLpLocation(true);
    try {
      const res = await api.post("/clients/lp-locations", { name, type: type ? type.toLowerCase() : null });
      const loc = res.data;
      setLpLocations((prev) => [...prev, loc].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((p) => ({ ...p, propane_location_id: String(loc.id) }));
      setNewLpLocationName("");
      setShowAddLpLocation(false);
    } catch (_) {}
    finally { setSavingLpLocation(false); }
  };

  // Gas locations are only surfaced to admin/director, so load them lazily when a
  // Gas request is being added/edited by such a user.
  useEffect(() => {
    if ((!addServiceIsGas && !editServiceIsGas) || !isAdminOrDirector || gasLocations.length > 0) return;
    api.get("/clients/gas-locations").then((res) => setGasLocations(res.data.items || [])).catch(() => {});
  }, [addServiceIsGas, editServiceIsGas, isAdminOrDirector, gasLocations.length]);

  const handleSaveGasLocation = async (setForm) => {
    const name = newGasLocationName.trim();
    if (!name) return;
    setSavingGasLocation(true);
    try {
      const res = await api.post("/clients/gas-locations", { name });
      const loc = res.data;
      setGasLocations((prev) => [...prev, loc].sort((a, b) => a.name.localeCompare(b.name)));
      setForm((p) => ({ ...p, gas_location_id: String(loc.id) }));
      setNewGasLocationName("");
      setShowAddGasLocation(false);
    } catch (_) {}
    finally { setSavingGasLocation(false); }
  };

  const openAddService = () => {
    setServiceFormError("");
    emergencyProvidedRef.current = false;
    emergencySkippedRef.current = false;
    setAddServiceForm({
      ...emptyServiceForm,
      service_date: formatDate(new Date()),
      quantity: 1,
      status: "Open",
      assigned_to: "",
      service_type_ids: [],
      notes: "",
      help_already_received: [],
      family_helped: "No",
      family_help_offered: "",
      // Pre-populate transport fields from client data
      transport_pickup_location_text: formatAddress(client) || "",
      transport_rider_category: client?.rider_category || (client?.spouse_name?.trim() ? "COUPLE" : ""),
    });
    setShowHelpSection(false);
    setShowFamilySection(false);
    setShowAddTransportProvider(false);
    setNewTransportProvider({ name: "", phone: "", city: "" });
    setTransportProviderError("");
    // Eagerly load transport lookups so they're ready if user selects transportation
    (async () => {
      setTransportLookupLoading(true);
      try {
        const [dRes, pRes] = await Promise.all([
          api.get("/drivers", { params: { active: "true" } }),
          api.get("/drivers/providers"),
        ]);
        setAllActiveTransportDrivers(dRes.data.items || []);
        setTransportDrivers(dRes.data.items || []);
        setTransportProviders(pRes.data.items || []);
      } catch (_) {
        // non-critical
      } finally {
        setTransportLookupLoading(false);
      }
    })();
    setShowAddService(true);
  };

  const openEditService = (s) => {
    setServiceFormError("");
    // Reset the emergency-contact prompt state so a client with no contact on
    // file is prompted again each time an existing service is edited.
    emergencyProvidedRef.current = false;
    emergencySkippedRef.current = false;
    const typeIds = normalizeTypeIdsFromService(s);
    const helpAlready = normalizeHelpAlready(s);

    // Find the ride linked to this service via appointment_id
    const matchingRide = rides.find((r) => r.appointment_id === s?.id) ?? null;

    // Determine current primary driver from matching ride
    const primaryAssignment = matchingRide
      ? (matchingRide.assignments || []).find(
          (a) => a.role === "PRIMARY" && ["ASSIGNED", "CONFIRMED"].includes(a.status)
        )
      : null;

    setEditingServiceId(s?.id);
    // Seed the internal notes list and reset the note editor UI.
    setEditServiceNotes(Array.isArray(s?.service_notes) ? s.service_notes : []);
    setAddingNote(false);
    setNewNoteText("");
    setEditingNoteId(null);
    setEditingNoteText("");
    setNoteError("");
    setEditServiceForm({
      service_type_ids: typeIds,
      service_date: formatDate(s?.service_date) || formatDate(new Date()),
      quantity:
        s?.quantity == null || s?.quantity === "" || Number.isNaN(Number(s.quantity))
          ? 1
          : Number(s.quantity),
      status: s?.status || "Open",
      assigned_to: s?.assigned_to ?? s?.assigned_to_id ?? s?.assigned_user_id ?? "",
      notes: s?.notes || "",
      internal_notes: s?.internal_notes || "",
      pickup_delivery_date: s?.pickup_delivery_date || "",
      bill_type: s?.bill_type || "",
      bill_provider: s?.bill_provider || "",
      bill_account_number: s?.bill_account_number || "",
      bill_amount_due: s?.bill_amount_due != null ? String(s.bill_amount_due) : "",
      bill_client_has: s?.bill_client_has != null ? String(s.bill_client_has) : "",
      bill_agency_dhhs: s?.bill_agency_dhhs != null ? String(s.bill_agency_dhhs) : "",
      bill_agency_listening_ear: s?.bill_agency_listening_ear != null ? String(s.bill_agency_listening_ear) : "",
      bill_agency_mmcaa: s?.bill_agency_mmcaa != null ? String(s.bill_agency_mmcaa) : "",
      bill_agency_salvation_army: s?.bill_agency_salvation_army != null ? String(s.bill_agency_salvation_army) : "",
      bill_agency_friend_family: s?.bill_agency_friend_family != null ? String(s.bill_agency_friend_family) : "",
      bill_agency_church: s?.bill_agency_church != null ? String(s.bill_agency_church) : "",
      bill_agency_other: s?.bill_agency_other != null ? String(s.bill_agency_other) : "",
      bill_hhh_amount: s?.bill_hhh_amount != null ? String(s.bill_hhh_amount) : "",
      tickets_requested: s?.tickets_requested ?? "",
      approved_by: s?.approved_by ?? null,
      bed_size: s?.bed_size || "",
      bed_twin_frames:      s?.bed_twin_frames      || 0,
      bed_twin_box_springs: s?.bed_twin_box_springs || 0,
      bed_twin_mattresses:  s?.bed_twin_mattresses  || 0,
      bed_full_frames:      s?.bed_full_frames      || 0,
      bed_full_box_springs: s?.bed_full_box_springs || 0,
      bed_full_mattresses:  s?.bed_full_mattresses  || 0,
      bed_delivery_method: s?.bed_delivery_method || "",
      service_location: s?.service_location || "",
      laundry_type: s?.laundry_type || "",
      laundry_needs_transport: false,
      food_type: s?.food_type || "",
      food_card_value: s?.food_card_value != null ? String(s.food_card_value) : "",
      food_card_qty: s?.food_card_qty != null ? String(s.food_card_qty) : "",
      gas_type: s?.gas_type || "",
      gas_amount: s?.gas_amount != null ? String(s.gas_amount) : "",
      gas_location_id: s?.gas_location_id != null ? String(s.gas_location_id) : "",
      gas_appointment_reason: s?.gas_appointment_reason || "",
      gas_appointment_verified: !!s?.gas_appointment_verified,
      gas_appt_date: s?.gas_appt_date || "",
      gas_destination: s?.gas_destination || "",
      propane_type: s?.propane_type || "",
      propane_gallons: s?.propane_gallons != null ? String(s.propane_gallons) : "",
      propane_fill_price: s?.propane_fill_price != null ? String(s.propane_fill_price) : "",
      propane_location_id: s?.propane_location_id != null ? String(s.propane_location_id) : "",
      propane_tank_service: s?.propane_tank_service || "",
      propane_tank_count: s?.propane_tank_count != null ? String(s.propane_tank_count) : "",
      propane_cost_per: s?.propane_cost_per != null ? String(s.propane_cost_per) : "",
      propane_tank_count_100: s?.propane_tank_count_100 != null ? String(s.propane_tank_count_100) : "",
      propane_cost_per_100: s?.propane_cost_per_100 != null ? String(s.propane_cost_per_100) : "",
      help_already_received: helpAlready,
      family_helped:
        String(s?.family_helped || s?.family_helped_yn || "")
          .trim()
          .toLowerCase() === "yes"
          ? "Yes"
          : "No",
      family_help_offered: s?.family_help_offered || s?.family_help_notes || "",
      // Transport fields — pre-populate from matching ride if found
      transport_ride_id: matchingRide?.id ?? null,
      transport_pickup_date: matchingRide
        ? matchingRide.pickup_datetime.slice(0, 10)
        : "",
      transport_pickup_time: matchingRide
        ? matchingRide.pickup_datetime.slice(11, 16)
        : "",
      transport_dropoff_location_text: matchingRide?.dropoff_location_text || "",
      transport_pickup_location_text: matchingRide?.pickup_location_text || formatAddress(client) || "",
      transport_provider_id: matchingRide?.provider_id ? String(matchingRide.provider_id) : "",
      transport_rider_category: matchingRide?.rider_category || client?.rider_category || (client?.spouse_name?.trim() ? "COUPLE" : ""),
      transport_driver_id: primaryAssignment?.driver_id ? String(primaryAssignment.driver_id) : "",
      transport_appointment_confirmed: !!matchingRide?.appointment_confirmed,
      transport_driver_confirmed: !!matchingRide?.driver_confirmed,
      transport_status: matchingRide?.status || "REQUESTED",
    });

    // Start these collapsed, but open them when the saved request has answers
    // so the checked boxes aren't hidden behind a chevron.
    setShowHelpSection(helpAlready.length > 0);
    setShowFamilySection(
      String(s?.family_helped || "").trim().toLowerCase() === "yes"
    );
    setShowAddTransportProvider(false);
    setNewTransportProvider({ name: "", phone: "", city: "" });
    setTransportProviderError("");
    // Load transport lookups
    (async () => {
      setTransportLookupLoading(true);
      try {
        const [dRes, pRes] = await Promise.all([
          api.get("/drivers", { params: { active: "true" } }),
          api.get("/drivers/providers"),
        ]);
        setAllActiveTransportDrivers(dRes.data.items || []);
        setTransportDrivers(dRes.data.items || []);
        setTransportProviders(pRes.data.items || []);
      } catch (_) {
        // non-critical
      } finally {
        setTransportLookupLoading(false);
      }
    })();

    setShowEditService(true);
  };

  const closeAddService = () => {
    setAddServiceQueueTotal(0);
    setShowAddService(false);
    if (addServiceQueueTotal > 0) loadDashboard();
  };
  const closeEditService = () => {
    setShowEditService(false);
    setEditingServiceId(null);
  };

  // Close-confirm prompt shared by the Add/Edit Service modals' top-right "X".
  // Clicking the X asks whether to save & close or just close.
  const [closeGuard, setCloseGuard] = useState(null); // null | "add" | "edit"
  const guardOnSave = async () => {
    if (closeGuard === "add") await handleAddServiceSave();
    else if (closeGuard === "edit") await handleEditServiceSave();
    // The save handlers close their modal on success; if a validation error is
    // shown instead, dismissing the guard returns the user to the form to fix it.
    setCloseGuard(null);
  };
  const guardOnDiscard = () => {
    if (closeGuard === "add") closeAddService();
    else if (closeGuard === "edit") closeEditService();
    setCloseGuard(null);
  };

  // ---- Internal notes (Edit Service modal) -------------------------------
  // All mutation endpoints return the full, ordered notes list for the service.
  const applyNotesResponse = (data) => {
    setEditServiceNotes(Array.isArray(data?.notes) ? data.notes : []);
    loadDashboard(); // keep the underlying services array in sync
  };

  const handleAddNote = async () => {
    const text = newNoteText.trim();
    if (!text) return;
    setNoteBusy(true);
    setNoteError("");
    try {
      const res = await api.post(`/clients/${id}/services/${editingServiceId}/notes`, { note: text });
      applyNotesResponse(res.data);
      // The backend auto-advances an untouched Open request to "Pending" when a
      // note is added. Reflect that in the dropdown, but never clobber a status
      // the user has already selected in the modal.
      if (res.data?.status) {
        setEditServiceForm((p) => (p.status === "Open" ? { ...p, status: res.data.status } : p));
      }
      setNewNoteText("");
      setAddingNote(false);
    } catch (err) {
      setNoteError(err.response?.data?.error || "Failed to add note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const startEditNote = (note) => {
    setEditingNoteId(note.id);
    setEditingNoteText(note.note || "");
    setNoteError("");
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteText("");
  };

  const handleSaveEditNote = async (noteId) => {
    const text = editingNoteText.trim();
    if (!text) return;
    setNoteBusy(true);
    setNoteError("");
    try {
      const res = await api.put(`/clients/${id}/services/${editingServiceId}/notes/${noteId}`, { note: text });
      applyNotesResponse(res.data);
      cancelEditNote();
    } catch (err) {
      setNoteError(err.response?.data?.error || "Failed to save note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    setNoteBusy(true);
    setNoteError("");
    try {
      const res = await api.delete(`/clients/${id}/services/${editingServiceId}/notes/${noteId}`);
      applyNotesResponse(res.data);
    } catch (err) {
      setNoteError(err.response?.data?.error || "Failed to delete note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const handleDeleteService = async () => {
    if (!editingServiceId) return;
    if (!window.confirm(
      "Delete this entire service request? This also removes any linked " +
      "transportation ride and cannot be undone."
    )) return;
    setSavingService(true);
    setServiceFormError("");
    try {
      await api.delete(`/clients/${id}/services/${editingServiceId}`);
      closeEditService();
      await loadDashboard();
    } catch (e) {
      setServiceFormError(e?.response?.data?.error || "Failed to delete service.");
    } finally {
      setSavingService(false);
    }
  };

  const [deletingClient, setDeletingClient] = useState(false);
  const handleDeleteClient = async () => {
    const name = headerName || `Client #${id}`;
    if (!window.confirm(
      `Delete client "${name}"? They will be removed from all lists and ` +
      `searches. This affects all of their service requests, rides, financials, ` +
      `and records. Continue?`
    )) return;
    setDeletingClient(true);
    try {
      await api.delete(`/clients/${id}`);
      navigate("/clients");
    } catch (e) {
      window.alert(e?.response?.data?.error || "Failed to delete client.");
      setDeletingClient(false);
    }
  };

  const openTaskModal = (serviceId) => {
    setTaskServiceId(serviceId);
    setTaskForm({ assigned_to: "", task: "" });
    setTaskError("");
    setShowTaskModal(true);
  };

  const handleAddTaskFromEdit = () => openTaskModal(editingServiceId);

  const handleAddTaskFromAdd = async () => {
    // Save the service first, then open the task modal with the new service ID
    setServiceFormError("");
    const form = addServiceForm;
    const currentTypeId = form.service_type_ids[0];
    const currentSt = serviceTypeById.get(Number(currentTypeId));
    const currentIsTransport = (currentSt?.category || "").toLowerCase().includes("transport");
    const msg = validateServiceForm({ ...form, service_type_ids: [currentTypeId] });
    if (msg) { setServiceFormError(msg); return; }

    setSavingService(true);
    try {
      const basePayload = buildServicePayload(
        { ...form, service_type_ids: [currentTypeId] },
        { includeCreatedBy: true }
      );
      // Transportation requests auto-approve on save; the ride is then managed
      // entirely from the Transportation Dashboard.
      if (currentIsTransport) basePayload.status = "Approved";
      const svcRes = await postToFirstWorking(
        [`/clients/${id}/services`, `/clients/${id}/service`, `/services`],
        basePayload
      );
      const createdServiceId = svcRes?.data?.items?.[0]?.id ?? null;
      if (currentIsTransport && form.transport_pickup_date && form.transport_pickup_time) {
        try {
          const rideRes = await api.post("/drivers/rides", {
            client_id: Number(id),
            appointment_id: createdServiceId,
            rider_category: form.transport_rider_category,
            pickup_datetime: `${form.transport_pickup_date}T${form.transport_pickup_time}:00`,
            pickup_location_text: form.transport_pickup_location_text || null,
            dropoff_location_text: form.transport_dropoff_location_text || null,
            provider_id: form.transport_provider_id ? Number(form.transport_provider_id) : null,
            appointment_confirmed: form.transport_appointment_confirmed,
            driver_confirmed: form.transport_driver_confirmed,
          });
          if (form.transport_driver_id) {
            await api.post(`/drivers/rides/${rideRes.data.id}/assign`, {
              driver_id: Number(form.transport_driver_id),
              auto_confirm: form.transport_driver_confirmed,
            });
          }
          await loadRides();
        } catch (_) { /* non-critical */ }
      }
      setAddServiceQueueTotal(0);
      setShowAddService(false);
      await loadDashboard();
      if (createdServiceId) openTaskModal(createdServiceId);
    } catch (e) {
      setServiceFormError(e?.response?.data?.error || "Failed to save service.");
    } finally {
      setSavingService(false);
    }
  };

  const handleSaveTask = async () => {
    if (!taskForm.task.trim()) { setTaskError("Task description is required."); return; }
    setTaskSaving(true);
    setTaskError("");
    try {
      await api.post("/tasks", {
        client_service_id: taskServiceId,
        assigned_to: taskForm.assigned_to || null,
        task: taskForm.task.trim(),
      });
      setShowTaskModal(false);
    } catch (e) {
      setTaskError(e?.response?.data?.error || "Failed to save task.");
    } finally {
      setTaskSaving(false);
    }
  };

  const validateServiceForm = (form) => {
    const typeIds = form?.service_type_ids || [];
    if (!Array.isArray(typeIds) || typeIds.length === 0) return "Select at least one service type.";
    if (!form?.service_date || String(form.service_date).trim().length < 8)
      return "Service Date is required.";

    const qtyNeeded = quantityRequiredForTypeIds(typeIds);
    if (qtyNeeded) {
      const q = Number(form.quantity);
      if (!Number.isFinite(q) || q <= 0) return "Quantity is required and must be greater than 0.";
    }

    // family help offered required if family_helped=yes?
    if (String(form.family_helped) === "Yes") {
      const txt = String(form.family_help_offered || "").trim();
      if (!txt) return "Please describe what help the family has been able to offer.";
    }

    // Transport field validation
    const isTransport = typeIds.some((tid) => {
      const st = serviceTypeById.get(Number(tid));
      return (st?.category || "").toLowerCase().includes("transport");
    });
    if (isTransport) {
      if (!form.transport_pickup_date) return "Transportation: Appointment date is required.";
      if (!form.transport_pickup_time) return "Transportation: Appointment time is required.";
      if (!form.transport_rider_category) return "Transportation: Rider category is required.";
    }

    return "";
  };

  const postToFirstWorking = async (candidates, body) => {
    let lastErr = null;
    for (const url of candidates) {
      try {
        const res = await api.post(url, body);
        return res;
      } catch (e) {
        const status = e?.response?.status;
        // A real validation error (4xx except 404) means the route exists — stop and surface it
        if (status && status !== 404) throw e;
        lastErr = e;
      }
    }
    throw lastErr || new Error("All POST endpoints failed");
  };

  const putToFirstWorking = async (candidates, body) => {
    let lastErr = null;
    for (const url of candidates) {
      try {
        await api.put(url, body);
        return;
      } catch (e) {
        const status = e?.response?.status;
        if (status && status !== 404) throw e;
        lastErr = e;
      }
    }
    throw lastErr || new Error("All PUT endpoints failed");
  };

  const buildServicePayload = (form, { includeCreatedBy } = { includeCreatedBy: false }) => {
    const typeIds = (form.service_type_ids || []).map((x) => Number(x)).filter(Number.isFinite);
    const qtyNeeded = quantityRequiredForTypeIds(typeIds);

    const helpArr = Array.isArray(form.help_already_received)
      ? form.help_already_received.map(String).filter(Boolean)
      : [];

    const payload = {
      client_id: Number(id),
      service_date: form.service_date,
      status: form.status || "Open",
      assigned_to: form.assigned_to || null,

      // multi + single fallback
      service_type_ids: typeIds,
      service_type_id: typeIds[0],

      quantity: qtyNeeded ? Number(form.quantity || 1) : null,

      // NEW fields
      notes: form.notes || "",
      internal_notes: form.internal_notes || "",
      pickup_delivery_date: form.pickup_delivery_date || null,
      bill_type: form.bill_type || null,
      bill_provider: form.bill_provider || null,
      bill_account_number: form.bill_account_number || null,
      bill_amount_due: form.bill_amount_due !== "" && form.bill_amount_due != null ? parseFloat(form.bill_amount_due) || null : null,
      bill_client_has: form.bill_client_has !== "" && form.bill_client_has != null ? parseFloat(form.bill_client_has) || null : null,
      bill_agency_dhhs: form.bill_agency_dhhs !== "" && form.bill_agency_dhhs != null ? parseFloat(form.bill_agency_dhhs) || null : null,
      bill_agency_listening_ear: form.bill_agency_listening_ear !== "" && form.bill_agency_listening_ear != null ? parseFloat(form.bill_agency_listening_ear) || null : null,
      bill_agency_mmcaa: form.bill_agency_mmcaa !== "" && form.bill_agency_mmcaa != null ? parseFloat(form.bill_agency_mmcaa) || null : null,
      bill_agency_salvation_army: form.bill_agency_salvation_army !== "" && form.bill_agency_salvation_army != null ? parseFloat(form.bill_agency_salvation_army) || null : null,
      bill_agency_friend_family: form.bill_agency_friend_family !== "" && form.bill_agency_friend_family != null ? parseFloat(form.bill_agency_friend_family) || null : null,
      bill_agency_church: form.bill_agency_church !== "" && form.bill_agency_church != null ? parseFloat(form.bill_agency_church) || null : null,
      bill_agency_other: form.bill_agency_other !== "" && form.bill_agency_other != null ? parseFloat(form.bill_agency_other) || null : null,
      bill_hhh_amount: form.bill_hhh_amount !== "" && form.bill_hhh_amount != null ? parseFloat(form.bill_hhh_amount) || null : null,
      tickets_requested: form.tickets_requested !== "" && form.tickets_requested != null ? Number(form.tickets_requested) || null : null,
      bed_size: form.bed_size || null,
      bed_delivery_method: form.bed_delivery_method || null,
      bed_twin_frames:      Number(form.bed_twin_frames)      || null,
      bed_twin_box_springs: Number(form.bed_twin_box_springs) || null,
      bed_twin_mattresses:  Number(form.bed_twin_mattresses)  || null,
      bed_full_frames:      Number(form.bed_full_frames)      || null,
      bed_full_box_springs: Number(form.bed_full_box_springs) || null,
      bed_full_mattresses:  Number(form.bed_full_mattresses)  || null,
      service_location: form.service_location || null,
      laundry_type: form.laundry_type || null,
      food_type: form.food_type || null,
      food_card_value: form.food_card_value !== "" && form.food_card_value != null ? parseFloat(form.food_card_value) || null : null,
      food_card_qty: form.food_card_qty !== "" && form.food_card_qty != null ? parseInt(form.food_card_qty) || null : null,
      gas_type: form.gas_type || null,
      gas_amount: form.gas_amount !== "" && form.gas_amount != null ? parseFloat(form.gas_amount) || null : null,
      gas_location_id: form.gas_location_id !== "" && form.gas_location_id != null ? parseInt(form.gas_location_id) || null : null,
      gas_appointment_reason: form.gas_appointment_reason || null,
      gas_appointment_verified: !!form.gas_appointment_verified,
      gas_appt_date: form.gas_appt_date || null,
      gas_destination: form.gas_destination || null,
      propane_type: form.propane_type || null,
      propane_gallons: form.propane_gallons !== "" && form.propane_gallons != null ? parseFloat(form.propane_gallons) || null : null,
      propane_fill_price: form.propane_fill_price !== "" && form.propane_fill_price != null ? parseFloat(form.propane_fill_price) || null : null,
      propane_location_id: form.propane_location_id !== "" && form.propane_location_id != null ? parseInt(form.propane_location_id) || null : null,
      propane_tank_service: form.propane_tank_service || null,
      propane_tank_count: form.propane_tank_count !== "" && form.propane_tank_count != null ? parseInt(form.propane_tank_count) || null : null,
      propane_cost_per: form.propane_cost_per !== "" && form.propane_cost_per != null ? parseFloat(form.propane_cost_per) || null : null,
      propane_tank_count_100: form.propane_tank_count_100 !== "" && form.propane_tank_count_100 != null ? parseInt(form.propane_tank_count_100) || null : null,
      propane_cost_per_100: form.propane_cost_per_100 !== "" && form.propane_cost_per_100 != null ? parseFloat(form.propane_cost_per_100) || null : null,
      help_already_received: helpArr,
      // CSV fallback in case backend expects a string
      help_already_received_csv: helpArr.join(", "),
      family_helped: form.family_helped || "No",
      family_help_offered: form.family_helped === "Yes" ? (form.family_help_offered || "") : "",
    };

    if (includeCreatedBy) {
      payload.created_by = getCurrentUserId();
      // created_at should be set server-side with NOW()
    }

    return payload;
  };

  const hasEmergencyContact = (c) =>
    !!(c?.emergency_contact_name?.trim() && c?.emergency_contact_phone?.trim());

  // Gas requests carry an appointment the gas is for. When saving one that
  // hasn't been marked Verified, remind staff to check it. OK saves anyway,
  // Cancel returns to the form. Returns true when the save may proceed.
  const gasVerifyReminderOk = (form, isGas) =>
    isGas && !form.gas_appointment_verified
      ? window.confirm(
          "Remember to verify the client has an appointment.\n\n" +
          "Click OK to save anyway, or Cancel to go back and mark it Verified."
        )
      : true;

  // Save the emergency contact to the client, then resume the service add.
  const saveEmergencyContact = async () => {
    const name = emergencyForm.name.trim();
    const phone = emergencyForm.phone.trim();
    if (!name || !phone) {
      setEmergencyError("Both an emergency contact name and phone are required.");
      return;
    }
    setEmergencySaving(true);
    setEmergencyError("");
    try {
      await api.put(`/clients/${id}`, {
        emergency_contact_name: name,
        emergency_contact_phone: phone,
      });
      setClient((prev) =>
        prev ? { ...prev, emergency_contact_name: name, emergency_contact_phone: phone } : prev
      );
      emergencyProvidedRef.current = true;
      setShowEmergencyModal(false);
      resumeAfterEmergencyPrompt();
    } catch (e) {
      setEmergencyError(e?.response?.data?.error || "Failed to save the emergency contact.");
    } finally {
      setEmergencySaving(false);
    }
  };

  // "Skip for now": let the user complete the request without an emergency
  // contact. The skip only suppresses the prompt for this add/edit session, so
  // the notice reappears on the next add/edit while the contact is still blank.
  const skipEmergencyContact = () => {
    emergencySkippedRef.current = true;
    setEmergencyError("");
    setShowEmergencyModal(false);
    resumeAfterEmergencyPrompt();
  };

  // Resume whichever save flow opened the prompt. When opened by checking the
  // Transportation box (resume = null) there is nothing to resume.
  const resumeAfterEmergencyPrompt = () => {
    const resume = emergencyResumeRef.current;
    emergencyResumeRef.current = null;
    if (resume === "add") handleAddServiceSave();
    else if (resume === "edit") handleEditServiceSave();
  };

  const handleAddServiceSave = async () => {
    setServiceFormError("");

    let form = addServiceForm;
    const currentTypeId = form.service_type_ids[0];
    const currentSt = serviceTypeById.get(Number(currentTypeId));
    const currentIsTransport = (currentSt?.category || "").toLowerCase().includes("transport");
    const currentIsBeds = (currentSt?.name || "").toLowerCase().includes("bed");
    const currentIsGas = (currentSt?.name || "").toLowerCase().includes("gas");
    const currentIsLaundryMinistry = (currentSt?.name || "").toLowerCase().includes("laundry") && form.laundry_type === "Ministry";
    const currentNeedsTransport = currentIsTransport || (currentIsLaundryMinistry && form.laundry_needs_transport);

    if (currentIsTransport && !form.service_date && form.transport_pickup_date) {
      form = { ...form, service_date: form.transport_pickup_date };
      setAddServiceForm(form);
    }

    // A pickup needs an emergency contact on file. If this client has none,
    // prompt for it before saving; the prompt resumes this save. The user can
    // skip, which completes the save but re-prompts on the next add/edit.
    if (currentNeedsTransport && !hasEmergencyContact(client) &&
        !emergencyProvidedRef.current && !emergencySkippedRef.current) {
      emergencyResumeRef.current = "add";
      setEmergencyForm({
        name: client?.emergency_contact_name || "",
        phone: client?.emergency_contact_phone || "",
      });
      setEmergencyError("");
      setShowEmergencyModal(true);
      return;
    }

    const msg = validateServiceForm({ ...form, service_type_ids: [currentTypeId] });
    if (msg) { setServiceFormError(msg); return; }

    // Remind staff to verify the appointment on an unverified Gas request.
    if (!gasVerifyReminderOk(form, currentIsGas)) return;

    // Set queue total the first time Save is clicked
    if (addServiceQueueTotal === 0) setAddServiceQueueTotal(form.service_type_ids.length);

    setSavingService(true);
    try {
      const basePayload = buildServicePayload(
        { ...form, service_type_ids: [currentTypeId] },
        { includeCreatedBy: true }
      );
      // Transportation requests auto-approve on save; the ride is then managed
      // entirely from the Transportation Dashboard.
      if (currentIsTransport) basePayload.status = "Approved";

      const svcRes = await postToFirstWorking(
        [`/clients/${id}/services`, `/clients/${id}/service`, `/services`],
        basePayload
      );
      const createdServiceId = svcRes?.data?.items?.[0]?.id ?? null;

      if (currentNeedsTransport && form.transport_pickup_date && form.transport_pickup_time) {
        try {
          const rideRes = await api.post("/drivers/rides", {
            client_id: Number(id),
            appointment_id: createdServiceId,
            rider_category: form.transport_rider_category,
            pickup_datetime: `${form.transport_pickup_date}T${form.transport_pickup_time}:00`,
            pickup_location_text: form.transport_pickup_location_text || null,
            dropoff_location_text: form.transport_dropoff_location_text || null,
            provider_id: form.transport_provider_id ? Number(form.transport_provider_id) : null,
            appointment_confirmed: form.transport_appointment_confirmed,
            driver_confirmed: form.transport_driver_confirmed,
          });
          if (form.transport_driver_id) {
            await api.post(`/drivers/rides/${rideRes.data.id}/assign`, {
              driver_id: Number(form.transport_driver_id),
              auto_confirm: form.transport_driver_confirmed,
            });
          }
          await loadRides();
        } catch (_) { /* non-critical */ }
      }

      const remainingTypes = form.service_type_ids.slice(1);
      if (remainingTypes.length > 0) {
        // Advance to next service type; keep common fields, reset type-specific ones
        setAddServiceForm((p) => ({
          ...p,
          service_type_ids: remainingTypes,
          status: "Open",
          assigned_to: "",
          notes: "",
          internal_notes: "",
          pickup_delivery_date: "",
          bill_type: "",
          bill_provider: "",
          bill_account_number: "",
          bill_amount_due: "",
          bill_client_has: "",
          bill_agency_dhhs: "",
          bill_agency_listening_ear: "",
          bill_agency_mmcaa: "",
          bill_agency_salvation_army: "",
          bill_agency_friend_family: "",
          bill_agency_church: "",
          bill_agency_other: "",
          bill_hhh_amount: "",
          tickets_requested: "",
          bed_twin_frames: 0,
          bed_twin_box_springs: 0,
          bed_twin_mattresses: 0,
          bed_full_frames: 0,
          bed_full_box_springs: 0,
          bed_full_mattresses: 0,
          bed_delivery_method: "",
          service_location: "",
          laundry_type: "",
          laundry_needs_transport: false,
          food_type: "",
          food_card_value: "",
          food_card_qty: "",
          gas_type: "",
          gas_amount: "",
          gas_location_id: "",
          gas_appointment_reason: "",
          gas_appointment_verified: false,
          gas_appt_date: "",
          gas_destination: "",
          propane_type: "",
          propane_gallons: "",
          propane_fill_price: "",
          propane_location_id: "",
          propane_tank_service: "",
          propane_tank_count: "",
          propane_cost_per: "",
          propane_tank_count_100: "",
          propane_cost_per_100: "",
          transport_ride_id: null,
          transport_pickup_date: "",
          transport_pickup_time: "08:00",
          transport_dropoff_location_text: "",
          transport_pickup_location_text: formatAddress(client) || "",
          transport_provider_id: "",
          transport_rider_category: client?.rider_category || (client?.spouse_name?.trim() ? "COUPLE" : ""),
          transport_driver_id: "",
          transport_appointment_confirmed: false,
          transport_driver_confirmed: false,
          transport_status: "REQUESTED",
        }));
        setServiceFormError("");
      } else {
        setAddServiceQueueTotal(0);
        setShowAddService(false);
        await loadDashboard();
      }
    } catch (e) {
      setServiceFormError(e?.response?.data?.error || "Failed to add service.");
    } finally {
      setSavingService(false);
    }
  };

  const handleAddTransportProvider = async () => {
    if (!newTransportProvider.name.trim()) {
      setTransportProviderError("Provider name is required.");
      return;
    }
    setTransportProviderError("");
    setAddingTransportProvider(true);
    try {
      const res = await api.post("/drivers/providers", {
        name: newTransportProvider.name.trim(),
        phone: newTransportProvider.phone || null,
        city: newTransportProvider.city || null,
      });
      const created = res.data;
      setTransportProviders((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name))
      );
      setAddServiceForm((p) => ({ ...p, transport_provider_id: String(created.id) }));
      setEditServiceForm((p) => ({ ...p, transport_provider_id: String(created.id) }));
      setNewTransportProvider({ name: "", phone: "", city: "" });
      setShowAddTransportProvider(false);
    } catch (e) {
      if (e?.response?.status === 409 && e.response.data?.provider) {
        const existing = e.response.data.provider;
        setTransportProviders((prev) => {
          if (prev.find((p) => p.id === existing.id)) return prev;
          return [...prev, existing].sort((a, b) => a.name.localeCompare(b.name));
        });
        setAddServiceForm((p) => ({ ...p, transport_provider_id: String(existing.id) }));
        setEditServiceForm((p) => ({ ...p, transport_provider_id: String(existing.id) }));
        setNewTransportProvider({ name: "", phone: "", city: "" });
        setShowAddTransportProvider(false);
      } else {
        setTransportProviderError(e?.response?.data?.error || "Failed to save provider.");
      }
    } finally {
      setAddingTransportProvider(false);
    }
  };

  const handleEditServiceSave = async () => {
    setServiceFormError("");
    if (!editingServiceId) {
      setServiceFormError("No service selected to edit.");
      return;
    }

    let form = editServiceForm;
    if (editServiceIsTransport && !form.service_date && form.transport_pickup_date) {
      form = { ...form, service_date: form.transport_pickup_date };
      setEditServiceForm(form);
    }

    // A pickup needs an emergency contact on file. If this client still has
    // none, prompt for it before saving; the prompt resumes this save. The user
    // can skip, which completes the save but re-prompts on the next add/edit.
    if (editShowsTransport && !hasEmergencyContact(client) &&
        !emergencyProvidedRef.current && !emergencySkippedRef.current) {
      emergencyResumeRef.current = "edit";
      setEmergencyForm({
        name: client?.emergency_contact_name || "",
        phone: client?.emergency_contact_phone || "",
      });
      setEmergencyError("");
      setShowEmergencyModal(true);
      return;
    }

    const msg = validateServiceForm(form);
    if (msg) {
      setServiceFormError(msg);
      return;
    }

    // Remind staff to verify the appointment on an unverified Gas request.
    if (!gasVerifyReminderOk(form, editServiceIsGas)) return;

    setSavingService(true);
    try {
      const payload = buildServicePayload(form, { includeCreatedBy: false });

      await putToFirstWorking(
        [
          `/clients/${id}/services/${editingServiceId}`,
          `/clients/${id}/service/${editingServiceId}`,
          `/services/${editingServiceId}`,
        ],
        payload
      );

      // Create or update the associated ride
      if (editShowsTransport && form.transport_pickup_date && form.transport_pickup_time) {
        try {
          const ridePayload = {
            rider_category: form.transport_rider_category,
            pickup_datetime: `${form.transport_pickup_date}T${form.transport_pickup_time}:00`,
            pickup_location_text: form.transport_pickup_location_text || null,
            dropoff_location_text: form.transport_dropoff_location_text || null,
            provider_id: form.transport_provider_id ? Number(form.transport_provider_id) : null,
            appointment_confirmed: form.transport_appointment_confirmed,
            driver_confirmed: form.transport_driver_confirmed,
          };

          if (form.transport_ride_id) {
            // Update existing ride
            await api.patch(`/drivers/rides/${form.transport_ride_id}`, {
              ...ridePayload,
              status: form.transport_status,
            });
            // Reassign driver if changed
            const existingRide = rides.find((r) => r.id === form.transport_ride_id);
            if (existingRide) {
              const primary = (existingRide.assignments || []).find(
                (a) => a.role === "PRIMARY" && ["ASSIGNED", "CONFIRMED"].includes(a.status)
              );
              const oldDriverId = primary?.driver_id ? String(primary.driver_id) : "";
              if (form.transport_driver_id !== oldDriverId) {
                if (primary) await api.delete(`/drivers/assignments/${primary.id}`);
                if (form.transport_driver_id) {
                  await api.post(`/drivers/rides/${form.transport_ride_id}/assign`, {
                    driver_id: Number(form.transport_driver_id),
                    auto_confirm: form.transport_driver_confirmed,
                  });
                }
              }
            }
          } else {
            // Create new ride linked to this service
            const rideRes = await api.post("/drivers/rides", {
              client_id: Number(id),
              appointment_id: editingServiceId,
              ...ridePayload,
            });
            if (form.transport_driver_id) {
              await api.post(`/drivers/rides/${rideRes.data.id}/assign`, {
                driver_id: Number(form.transport_driver_id),
                auto_confirm: form.transport_driver_confirmed,
              });
            }
          }
          await loadRides();
        } catch (_) {
          // Service saved; ride update is non-critical
        }
      }

      setShowEditService(false);
      setEditingServiceId(null);
      await loadDashboard();
    } catch (e) {
      setServiceFormError(e?.response?.data?.error || "Failed to update service.");
    } finally {
      setSavingService(false);
    }
  };

  const renderServiceTypeBadges = (s) => {
    const typeIds = normalizeTypeIdsFromService(s);
    if (!typeIds.length) return <Badge bg="secondary">No Type</Badge>;

    const statusLower = (s?.status || "").toLowerCase();
    const isComplete = statusLower === "complete";
    const isPending = statusLower === "pending";
    let bg, text;
    if (statusLower === "approved") {
      bg = "success"; text = null;
    } else if (statusLower === "denied") {
      bg = "danger"; text = null;
    } else if (statusLower === "closed") {
      bg = "secondary"; text = null;
    } else if (isComplete || isPending) {
      bg = null; text = null;
    } else {
      bg = "warning"; text = "dark";
    }

    return typeIds.map((tid) => {
      const st = serviceTypeById.get(Number(tid));
      const label = st?.name ? st.name : `Type #${tid}`;
      if (isComplete || isPending) {
        return (
          <span
            key={tid}
            className="badge me-1"
            style={{ backgroundColor: isPending ? "#fd7e14" : "#6f42c1", color: "#fff" }}
          >
            {label}
          </span>
        );
      }
      return (
        <Badge bg={bg} text={text} key={tid} className="me-1">
          {label}
        </Badge>
      );
    });
  };

  const assignedToLabel = (s) => {
    const uid = s?.assigned_to ?? s?.assigned_to_id ?? s?.assigned_user_id ?? null;
    if (!uid) return "";
    return userLabelById.get(Number(uid)) || `User #${uid}`;
  };

  const lookupWarning =
    serviceTypes.length === 0 || users.length === 0
      ? "Lookup routes (service types/users) may not be implemented yet. You can still enter notes and save once backend routes exist."
      : "";

  return (
    <Container fluid className="py-3 px-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            <h3 className="mb-0">{headerName || `Client #${id}`}</h3>

            {/* Rolling 12-month marker; renders nothing once it has expired. */}
            <SalvationArmyBadge client={client} size={26} />

            {showLives ? <Badge bg="success">Lives in Clare County</Badge> : null}
            {showChurch ? <Badge bg="success">Church in Clare</Badge> : null}
            {showKids ? <Badge bg="success">Kids in Clare Schools</Badge> : null}
            {showVerify ? <Badge bg="success">Permission to Verify</Badge> : null}
          </div>

          <div className="text-muted">
            <span className="fw-bold">Last Helped: {lastHelped || "—"}</span>
            <span className="mx-2">•</span>
            <span>Intake: {intakeDate || "—"}</span>
          </div>

          {(intakeStatusBadge || navBadge) ? (
            <div className="mt-1 d-flex gap-2 flex-wrap">
              {intakeStatusBadge ? (
                <Badge
                  bg={intakeStatusBadge.bg}
                  text={intakeStatusBadge.text}
                  className="px-2 py-1"
                >
                  {intakeStatusBadge.label}
                </Badge>
              ) : null}
              {navBadge ? (
                <Badge
                  bg={navBadge.bg}
                  text={navBadge.text}
                  className="px-2 py-1"
                >
                  {navBadge.label}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="d-flex gap-2 align-items-center">
          {showFunctionalLimitations ? (
            <Badge
              bg="warning"
              text="dark"
              className="px-2 py-2"
              title={functionalLimitations}
              style={{ cursor: "default", maxWidth: 420 }}
            >
              {functionalLimitations}
            </Badge>
          ) : null}

          {showSafetyAlert ? (
            <Badge
              bg="danger"
              className="px-2 py-2 d-inline-flex align-items-center gap-1"
              title={safetyAlerts}
              style={{ cursor: "default" }}
            >
              <i className="bi bi-shield-fill" />
              Safety Alert
            </Badge>
          ) : null}

          {showSpecialCircumstances ? (
            <Badge
              bg="success"
              className="px-2 py-2 d-inline-flex align-items-center gap-1"
              title={specialCircumstances}
              style={{ cursor: "default" }}
            >
              <i className="bi bi-emoji-smile-fill" />
              Special Circumstances
            </Badge>
          ) : null}

          <Button as={Link} to="/clients" variant="outline-secondary">
            Back to Clients
          </Button>
          {(user?.roles?.includes("ADMIN") ||
            user?.roles?.includes("DIRECTOR") ||
            user?.roles?.includes("INTAKE_WORKER")) && (
            <Button
              variant="outline-primary"
              onClick={handlePrintPacket}
              disabled={printingPacket}
            >
              <i className="bi bi-printer me-1" />
              {printingPacket ? "Generating…" : "Print Intake Packet"}
            </Button>
          )}
          <Button as={Link} to={`/clients/${id}`} variant="primary">
            Edit Client
          </Button>
          {isAdminOrDirector && (
            <Button
              variant="outline-danger"
              onClick={handleDeleteClient}
              disabled={deletingClient}
            >
              <i className="bi bi-trash me-1" />
              {deletingClient ? "Deleting…" : "Delete Client"}
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Row className="g-3">
          {/* Left sidebar */}
          <Col lg={3} md={4}>
            <div style={{ position: "sticky", top: "1rem" }}>
              <Card className="shadow-sm">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <div>Services</div>
                  <Button size="sm" variant="outline-primary" onClick={openAddService}>
                    Add
                  </Button>
                </Card.Header>

                <div style={{ maxHeight: "calc(100vh - 220px)", overflowY: "auto" }}>
                  <Card.Body style={{ padding: 0 }}>
                    {services.length === 0 ? (
                      <div className="p-3 text-muted">No services recorded.</div>
                    ) : (
                      <ListGroup variant="flush">
                        {services.map((s) => {
                          const assigned = assignedToLabel(s);
                          return (
                            <ListGroup.Item key={s.id} className="py-2">
                              <div className="d-flex justify-content-between align-items-start gap-2">
                                <div className="fw-semibold">
                                  {formatDate(s.service_date)} {renderServiceTypeBadges(s)}{" "}
                                  {(() => {
                                    const typeIds = normalizeTypeIdsFromService(s);
                                    const needsQty = typeIds.some((tid) => {
                                      const name = (serviceTypeById.get(Number(tid))?.name || "").toLowerCase();
                                      return name.includes("bed") || name.includes("furniture") || name.includes("kitchen");
                                    });
                                    return needsQty && Number.isFinite(Number(s.quantity)) && Number(s.quantity) > 0 ? (
                                      <Badge bg="info">Qty {Number(s.quantity)}</Badge>
                                    ) : null;
                                  })()}
                                </div>

                                <Button
                                  variant="link"
                                  className="p-0"
                                  title="Edit service"
                                  onClick={() => openEditService(s)}
                                >
                                  <i className="bi bi-pencil" style={{ fontSize: "1.1rem" }} />
                                </Button>
                              </div>

                              {(() => {
                                const typeIds = normalizeTypeIdsFromService(s);
                                const isTransport = typeIds.some((tid) => {
                                  const st = serviceTypeById.get(Number(tid));
                                  return (st?.category || "").toLowerCase().includes("transport");
                                });
                                if (!isTransport) return null;
                                const matchingRide = rides.find((r) => r.appointment_id === s.id);
                                if (!matchingRide) return null;
                                const primaryAssignment = (matchingRide.assignments || []).find(
                                  (a) => a.role === "PRIMARY" && a.status !== "CANCELLED"
                                );
                                const apptConfirmed = !!matchingRide.appointment_confirmed;
                                // pickup_datetime is a plain local string ("YYYY-MM-DDTHH:MM:SS");
                                // format from slices to avoid timezone shifts.
                                const pickup = matchingRide.pickup_datetime || "";
                                const pickupDate = pickup ? formatDate(pickup) : "";
                                const hm = pickup.slice(11, 16);
                                let pickupTime = "";
                                if (hm) {
                                  const [h, m] = hm.split(":").map(Number);
                                  const ampm = h >= 12 ? "PM" : "AM";
                                  const h12 = h % 12 === 0 ? 12 : h % 12;
                                  pickupTime = `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
                                }
                                const pickupLabel = [pickupDate, pickupTime].filter(Boolean).join(" ");
                                return (
                                  <>
                                    <div className="text-muted mt-1" style={{ fontSize: "0.9em" }}>
                                      Appointment:{" "}
                                      {pickupLabel ? (
                                        <span className="fw-semibold">{pickupLabel} — </span>
                                      ) : null}
                                      <span className={apptConfirmed ? "text-success fw-semibold" : "fw-semibold"}>
                                        {apptConfirmed ? "Confirmed" : "Not Confirmed"}
                                      </span>
                                    </div>
                                    {primaryAssignment?.driver_name ? (
                                      <div className="text-muted mt-1" style={{ fontSize: "0.9em" }}>
                                        Driver Assigned: <span className="fw-semibold">{primaryAssignment.driver_name}</span>
                                      </div>
                                    ) : null}
                                  </>
                                );
                              })()}

                              {assigned ? (
                                <div className="text-muted mt-1" style={{ fontSize: "0.9em" }}>
                                  Assigned To: <span className="fw-semibold">{assigned}</span>
                                </div>
                              ) : null}

                              {s.status && s.status !== "Open" && (
                                <div className="mt-1" style={{ whiteSpace: "pre-wrap", fontSize: "0.85em" }}>
                                  <span className={
                                    s.status === "Approved" ? "text-success fw-semibold" :
                                    s.status === "Denied"   ? "text-danger fw-semibold" :
                                    s.status === "Closed"   ? "text-secondary fw-semibold" :
                                    "fw-semibold"
                                  } style={
                                    s.status === "Complete" ? { color: "#6f42c1" } :
                                    s.status === "Pending"  ? { color: "#fd7e14" } :
                                    undefined
                                  }>
                                    {s.status === "Approved" && s.approved_by_name
                                      ? `Approved by ${s.approved_by_name}`
                                      : s.status}
                                  </span>
                                  {(s.status_note || s.notes) ? ` — ${s.status_note || s.notes}` : ""}
                                </div>
                              )}
                            </ListGroup.Item>
                          );
                        })}
                      </ListGroup>
                    )}
                  </Card.Body>
                </div>
              </Card>
            </div>
          </Col>

          {/* Main content */}
          <Col lg={9} md={8}>
            <Row className="g-3">
              <Col lg={12}>
                <Card>
                  <Card.Header>Contact Information</Card.Header>
                  <Card.Body>
                    <Row className="g-3">
                      <Col md={4}>
                        <div className="text-muted">Phone</div>
                        <div className="fw-semibold">{phone || "—"}</div>
                      </Col>
                      <Col md={8}>
                        <div className="text-muted">Address</div>
                        <div className="fw-semibold">{address || "—"}</div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={12}>
                <Card>
                  <Card.Header>Intake Summary</Card.Header>
                  <Card.Body>
                    {client?.intake_summary ? (
                      <div style={{ whiteSpace: "pre-wrap" }}>{client.intake_summary}</div>
                    ) : (
                      <div className="text-muted">No intake summary.</div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={12}>
                <Card>
                  <Card.Header>Household Members</Card.Header>
                  <Card.Body>
                    {familyMembers.length === 0 ? (
                      <div className="text-muted">No household members on file.</div>
                    ) : (
                      <ListGroup variant="flush">
                        {familyMembers.map((m) => (
                          <ListGroup.Item key={m.id} className="px-0">
                            <Row className="g-2 align-items-start">
                              <Col md={4}>
                                <div className="text-muted">Name</div>
                                <div className="fw-semibold">
                                  {m.full_name || "—"}
                                  {m.is_child && (
                                    <Badge bg="info" className="ms-2" style={{ fontSize: "0.75em" }}>Child</Badge>
                                  )}
                                </div>
                                {m.relationship ? (
                                  <div className="text-muted" style={{ fontSize: "0.9em" }}>
                                    {m.relationship}
                                  </div>
                                ) : null}
                              </Col>

                              <Col md={3}>
                                <div className="text-muted">Birth Date</div>
                                <div className="fw-semibold">
                                  {formatDate(m.birth_date) || "—"}
                                </div>
                              </Col>

                              <Col md={2}>
                                <div className="text-muted">Age</div>
                                <div className="fw-semibold">{calcAge(m.birth_date) || "—"}</div>
                              </Col>

                              <Col md={3}>
                                <div className="text-muted">Notes</div>
                                <div style={{ whiteSpace: "pre-wrap" }}>{m.notes || "—"}</div>
                              </Col>
                            </Row>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      )}

      {/* ---------- Add Service Modal ---------- */}
      <Modal
        show={showAddService}
        onHide={closeAddService}
        centered
        size="lg"
      >
        <Modal.Header className="d-block">
          {clientInfoLine}
          <div className="d-flex align-items-center">
            <Modal.Title className="h5 mb-0">
              {addServiceQueueTotal > 1
                ? `Add Service (${addServiceQueueTotal - addServiceForm.service_type_ids.length + 1} of ${addServiceQueueTotal})`
                : "Add Service"}
            </Modal.Title>
            <div className="ms-auto d-flex align-items-center gap-2">
              {(user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR")) && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddTaskFromAdd}
                  disabled={savingService}
                >
                  <i className="bi bi-plus-circle me-1" />Add Task
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleAddServiceSave}
                disabled={savingService}
              >
                {savingService ? "Saving..." : "Save"}
              </Button>
              <CloseButton aria-label="Close" onClick={() => setCloseGuard("add")} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {lookupWarning ? <Alert variant="warning">{lookupWarning}</Alert> : null}
          {serviceFormError ? <Alert variant="danger">{serviceFormError}</Alert> : null}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Service Date</Form.Label>
              <Form.Control
                type="date"
                value={addServiceForm.service_date || ""}
                onChange={(e) => setAddServiceForm((p) => ({ ...p, service_date: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Services Requested</Form.Label>
              {addServiceQueueTotal > 0 ? (
                <div className="fw-semibold">
                  {serviceTypeById.get(Number(addServiceForm.service_type_ids[0]))?.name || `Type #${addServiceForm.service_type_ids[0]}`}
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {(serviceTypes || []).map((st) => (
                    <Form.Check
                      key={st.id}
                      type="checkbox"
                      id={`add-svc-type-${st.id}`}
                      label={st.name || `Type #${st.id}`}
                      checked={(addServiceForm.service_type_ids || []).includes(st.id)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setAddServiceForm((p) => ({
                          ...p,
                          service_type_ids: checked
                            ? [...p.service_type_ids, st.id]
                            : p.service_type_ids.filter((x) => x !== st.id),
                        }));
                        // Prompt for an emergency contact the moment Transportation
                        // is checked, if the client doesn't have one on file.
                        const isTransport = (st.category || "").toLowerCase().includes("transport");
                        if (checked && isTransport && !hasEmergencyContact(client) &&
                            !emergencyProvidedRef.current && !emergencySkippedRef.current) {
                          emergencyResumeRef.current = null;
                          setEmergencyForm({
                            name: client?.emergency_contact_name || "",
                            phone: client?.emergency_contact_phone || "",
                          });
                          setEmergencyError("");
                          setShowEmergencyModal(true);
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </Form.Group>

            {addServiceIsBusCard && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-ticket-perforated me-2" />Bus Card Details
                </div>
                {client?.uses_transit && (
                  <p className="mb-3 fw-semibold">
                    Transit Fare: ${client.transit_fare}
                  </p>
                )}
                {isAdminOrDirector ? (
                  <Form.Group className="mb-3">
                    <Form.Label># of Cards</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={addServiceForm.tickets_requested || ""}
                      onChange={(e) => setAddServiceForm((p) => ({ ...p, tickets_requested: e.target.value }))}
                    />
                  </Form.Group>
                ) : adminOnlyDetailsNote}
              </>
            )}

            {addServiceIsLaundry && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-basket me-2" />Laundry Details
                </div>
                {isAdminOrDirector ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Type</Form.Label>
                      <div className="d-flex gap-4">
                        {["Card", "Ministry"].map((t) => (
                          <Form.Check
                            key={t}
                            type="radio"
                            id={`add-laundry-type-${t}`}
                            name="add-laundry-type"
                            label={t}
                            checked={addServiceForm.laundry_type === t}
                            onChange={() => setAddServiceForm((p) => ({ ...p, laundry_type: t }))}
                          />
                        ))}
                      </div>
                    </Form.Group>
                    {addServiceIsLaundryMinistry && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <div className="d-flex gap-4">
                            {["Clare", "Harrison"].map((loc) => (
                              <Form.Check
                                key={loc}
                                type="radio"
                                id={`add-laundry-loc-${loc}`}
                                name="add-laundry-location"
                                label={loc}
                                checked={addServiceForm.service_location === loc}
                                onChange={() => setAddServiceForm((p) => ({ ...p, service_location: loc }))}
                              />
                            ))}
                          </div>
                        </Form.Group>
                        <Form.Check
                          type="checkbox"
                          id="add-laundry-needs-transport"
                          label="Transportation required"
                          className="mb-3"
                          checked={!!addServiceForm.laundry_needs_transport}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, laundry_needs_transport: e.target.checked }))}
                        />
                      </>
                    )}
                  </>
                ) : adminOnlyDetailsNote}
              </>
            )}

            {addServiceIsFood && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-bag me-2" />Food Details
                </div>
                {isAdminOrDirector ? (
                <>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Card", "Pantry"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`add-food-type-${t}`}
                        name="add-food-type"
                        label={t}
                        checked={addServiceForm.food_type === t}
                        onChange={() => setAddServiceForm((p) => ({ ...p, food_type: t }))}
                      />
                    ))}
                  </div>
                </Form.Group>
                {addServiceIsFoodCard && (
                  <Row className="g-2 align-items-end mb-3">
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Value ($)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          style={{ width: "110px" }}
                          value={addServiceForm.food_card_value}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, food_card_value: e.target.value }))}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Qty</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          style={{ width: "80px" }}
                          value={addServiceForm.food_card_qty}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, food_card_qty: e.target.value }))}
                          placeholder="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Total</Form.Label>
                        <div className="fw-semibold pt-1">
                          ${((parseFloat(addServiceForm.food_card_value) || 0) * (parseInt(addServiceForm.food_card_qty) || 0)).toFixed(2)}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                </>
                ) : adminOnlyDetailsNote}
              </>
            )}

            {addServiceIsGas && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-fuel-pump me-2" />Gas Details
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Card", "Cash"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`add-gas-type-${t}`}
                        name="add-gas-type"
                        label={t}
                        // Card is not currently offered — grayed out, Cash only.
                        disabled={t === "Card"}
                        checked={addServiceForm.gas_type === t}
                        onChange={() => setAddServiceForm((p) => ({ ...p, gas_type: t, gas_location_id: "" }))}
                      />
                    ))}
                  </div>
                </Form.Group>
                {isAdminOrDirector ? (
                  <Form.Group className="mb-3">
                    <Form.Label>Amount ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      style={{ width: "130px" }}
                      value={addServiceForm.gas_amount}
                      onChange={(e) => setAddServiceForm((p) => ({ ...p, gas_amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>
                ) : adminOnlyDetailsNote}
                {isAdminOrDirector && (
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Select
                      style={{ maxWidth: "280px" }}
                      value={addServiceForm.gas_location_id}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setShowAddGasLocation(true);
                          setNewGasLocationName("");
                        } else {
                          setAddServiceForm((p) => ({ ...p, gas_location_id: e.target.value }));
                          setShowAddGasLocation(false);
                        }
                      }}
                    >
                      <option value="">— Select location —</option>
                      {gasLocations.map((loc) => (
                        <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                      ))}
                      <option value="__new__">+ Add new location…</option>
                    </Form.Select>
                    {showAddGasLocation && (
                      <div className="d-flex gap-2 mt-2">
                        <Form.Control
                          type="text" placeholder="New location name" style={{ maxWidth: "220px" }}
                          value={newGasLocationName}
                          onChange={(e) => setNewGasLocationName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveGasLocation(setAddServiceForm); } }}
                        />
                        <Button variant="outline-primary" size="sm"
                          disabled={savingGasLocation || !newGasLocationName.trim()}
                          onClick={() => handleSaveGasLocation(setAddServiceForm)}>
                          {savingGasLocation ? "Saving…" : "Add"}
                        </Button>
                        <Button variant="outline-secondary" size="sm"
                          onClick={() => { setShowAddGasLocation(false); setNewGasLocationName(""); }}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control
                    type="date"
                    style={{ width: "180px" }}
                    value={addServiceForm.gas_appt_date}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, gas_appt_date: e.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Destination</Form.Label>
                  <Form.Control
                    type="text"
                    value={addServiceForm.gas_destination}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, gas_destination: e.target.value }))}
                    placeholder="Where the client is traveling to"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={addServiceForm.gas_appointment_reason}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, gas_appointment_reason: e.target.value }))}
                    placeholder="Appointment the gas is for"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="add-gas-appointment-verified"
                    label="Verified"
                    checked={addServiceForm.gas_appointment_verified}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, gas_appointment_verified: e.target.checked }))}
                  />
                </Form.Group>
              </>
            )}

            {addServiceIsPropane && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-fire me-2" />Propane Details
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Pig", "Tank"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`add-propane-type-${t}`}
                        name="add-propane-type"
                        label={t}
                        checked={addServiceForm.propane_type === t}
                        onChange={() => setAddServiceForm((p) => ({ ...p, propane_type: t, propane_location_id: "" }))}
                      />
                    ))}
                  </div>
                </Form.Group>

                {addServiceForm.propane_type === "Pig" && (
                  <Row className="g-2 align-items-end mb-3">
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Fill Amount (gal)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.001" style={{ width: "130px" }}
                          value={addServiceForm.propane_gallons}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_gallons: e.target.value }))}
                          placeholder="0.000"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Fill Price ($)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.01" style={{ width: "120px" }}
                          value={addServiceForm.propane_fill_price}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_fill_price: e.target.value }))}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {addServiceIsPropaneTank && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Service</Form.Label>
                      <div className="d-flex gap-4">
                        {["Refill", "Exchange"].map((opt) => (
                          <Form.Check
                            key={opt}
                            type="radio"
                            id={`add-propane-tank-svc-${opt}`}
                            name="add-propane-tank-service"
                            label={opt}
                            checked={addServiceForm.propane_tank_service === opt}
                            onChange={() => setAddServiceForm((p) => ({
                              ...p,
                              propane_tank_service: opt,
                              propane_location_id: "",
                              propane_tank_count_100: "",
                              propane_cost_per_100: "",
                            }))}
                          />
                        ))}
                      </div>
                    </Form.Group>

                    {addServiceForm.propane_tank_service && (
                      <>
                        <table className="mb-3" style={{ borderSpacing: "0 6px", borderCollapse: "separate" }}>
                          <thead>
                            <tr>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em", minWidth: 48 }}>Size</th>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Qty</th>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Cost Per ($)</th>
                              <th className="text-muted fw-normal" style={{ fontSize: "0.85em" }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="pe-3 align-middle fw-semibold">20#</td>
                              <td className="pe-3">
                                <Form.Control
                                  type="number" min="0" step="1" style={{ width: "80px" }}
                                  value={addServiceForm.propane_tank_count}
                                  onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_tank_count: e.target.value }))}
                                  placeholder="0"
                                />
                              </td>
                              <td className="pe-3">
                                <Form.Control
                                  type="number" min="0" step="0.01" style={{ width: "100px" }}
                                  value={addServiceForm.propane_cost_per}
                                  onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_cost_per: e.target.value }))}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text" readOnly style={{ width: "100px" }}
                                  value={
                                    addServiceForm.propane_tank_count && addServiceForm.propane_cost_per
                                      ? `$${(parseFloat(addServiceForm.propane_tank_count) * parseFloat(addServiceForm.propane_cost_per)).toFixed(2)}`
                                      : "—"
                                  }
                                />
                              </td>
                            </tr>
                            {addServiceForm.propane_tank_service === "Refill" && (
                              <tr>
                                <td className="pe-3 align-middle fw-semibold">100#</td>
                                <td className="pe-3">
                                  <Form.Control
                                    type="number" min="0" step="1" style={{ width: "80px" }}
                                    value={addServiceForm.propane_tank_count_100}
                                    onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_tank_count_100: e.target.value }))}
                                    placeholder="0"
                                  />
                                </td>
                                <td className="pe-3">
                                  <Form.Control
                                    type="number" min="0" step="0.01" style={{ width: "100px" }}
                                    value={addServiceForm.propane_cost_per_100}
                                    onChange={(e) => setAddServiceForm((p) => ({ ...p, propane_cost_per_100: e.target.value }))}
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="text" readOnly style={{ width: "100px" }}
                                    value={
                                      addServiceForm.propane_tank_count_100 && addServiceForm.propane_cost_per_100
                                        ? `$${(parseFloat(addServiceForm.propane_tank_count_100) * parseFloat(addServiceForm.propane_cost_per_100)).toFixed(2)}`
                                        : "—"
                                    }
                                  />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <Form.Select
                            style={{ maxWidth: "280px" }}
                            value={addServiceForm.propane_location_id}
                            onChange={(e) => {
                              if (e.target.value === "__new__") {
                                setShowAddLpLocation(true);
                                setNewLpLocationName("");
                              } else {
                                setAddServiceForm((p) => ({ ...p, propane_location_id: e.target.value }));
                                setShowAddLpLocation(false);
                              }
                            }}
                          >
                            <option value="">— Select location —</option>
                            {lpLocations
                              .filter((loc) => !loc.type || loc.type === addServiceForm.propane_tank_service.toLowerCase())
                              .map((loc) => (
                                <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                              ))}
                            <option value="__new__">+ Add new location…</option>
                          </Form.Select>
                          {showAddLpLocation && (
                            <div className="d-flex gap-2 mt-2">
                              <Form.Control
                                type="text" placeholder="New location name" style={{ maxWidth: "220px" }}
                                value={newLpLocationName}
                                onChange={(e) => setNewLpLocationName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveLpLocation(setAddServiceForm, addServiceForm.propane_tank_service); } }}
                              />
                              <Button variant="outline-primary" size="sm"
                                disabled={savingLpLocation || !newLpLocationName.trim()}
                                onClick={() => handleSaveLpLocation(setAddServiceForm, addServiceForm.propane_tank_service)}>
                                {savingLpLocation ? "Saving…" : "Add"}
                              </Button>
                              <Button variant="outline-secondary" size="sm"
                                onClick={() => { setShowAddLpLocation(false); setNewLpLocationName(""); }}>
                                Cancel
                              </Button>
                            </div>
                          )}
                        </Form.Group>
                      </>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="add-propane-delivery"
                        label={<span className="text-muted">Delivery <small>(coming soon)</small></span>}
                        disabled
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}

            {addServiceIsBillPay && (() => {
              const { stillNeeded, clientOwes } = calcBillPay(addServiceForm);
              return (
                <>
                  <hr className="my-3" />
                  <div className="fw-semibold mb-3">
                    <i className="bi bi-receipt me-2" />Help Pay a Bill Details
                  </div>

                  <Row className="g-2 mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Bill Type</Form.Label>
                        <Form.Control
                          list="add-bill-type-options"
                          value={addServiceForm.bill_type || ""}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_type: e.target.value }))}
                          placeholder="Select or type…"
                        />
                        <datalist id="add-bill-type-options">
                          {BILL_PAY_DEFAULT_TYPES.map((t) => <option key={t} value={t} />)}
                        </datalist>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Provider</Form.Label>
                        <Form.Control
                          type="text"
                          value={addServiceForm.bill_provider || ""}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_provider: e.target.value }))}
                          placeholder="e.g. Consumers Energy"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Account Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={addServiceForm.bill_account_number || ""}
                          onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_account_number: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Amount Due</Form.Label>
                    <Form.Control
                      type="number" min="0" step="0.01" style={{ maxWidth: "160px" }}
                      value={addServiceForm.bill_amount_due || ""}
                      onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_amount_due: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Client Has</Form.Label>
                    <Form.Control
                      type="number" min="0" step="0.01" style={{ maxWidth: "160px" }}
                      value={addServiceForm.bill_client_has || ""}
                      onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_client_has: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>

                  <div className="fw-semibold mb-2" style={{ fontSize: "0.9em" }}>Agency Contributions</div>
                  <table className="mb-3 w-100" style={{ maxWidth: "400px" }}>
                    <tbody>
                      {BILL_PAY_AGENCIES.map(({ key, label }) => (
                        <tr key={key}>
                          <td className="pe-3 py-1 align-middle" style={{ width: "160px" }}>{label}</td>
                          <td className="py-1">
                            <Form.Control
                              type="number" min="0" step="0.01" style={{ width: "120px" }}
                              value={addServiceForm[key] || ""}
                              onChange={(e) => setAddServiceForm((p) => ({ ...p, [key]: e.target.value }))}
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Row className="g-2 mb-3 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <span className="fw-semibold">Amount Still Needed</span>
                    </Col>
                    <Col xs="auto">
                      <span className={`fw-semibold ${stillNeeded < 0 ? "text-danger" : ""}`}>
                        ${stillNeeded.toFixed(2)}
                      </span>
                    </Col>
                  </Row>

                  <Row className="g-2 mb-3 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <Form.Label className="mb-0">HHH Amount</Form.Label>
                    </Col>
                    <Col xs="auto">
                      <Form.Control
                        type="number" min="0" step="0.01" style={{ width: "120px" }}
                        value={addServiceForm.bill_hhh_amount || ""}
                        onChange={(e) => setAddServiceForm((p) => ({ ...p, bill_hhh_amount: e.target.value }))}
                        placeholder="0.00"
                      />
                    </Col>
                  </Row>

                  <Row className="g-2 mb-2 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <span className="fw-semibold">Client Must Provide</span>
                    </Col>
                    <Col xs="auto">
                      <span className={`fw-semibold ${clientOwes < 0 ? "text-danger" : "text-primary"}`}>
                        ${clientOwes.toFixed(2)}
                      </span>
                    </Col>
                  </Row>
                </>
              );
            })()}

            {addServiceNeedsDelivery && (
              <>
                <hr className="my-3" />
                <Form.Group className="mb-3">
                  <Form.Label>Delivery Method</Form.Label>
                  <div className="d-flex gap-4 mt-1">
                    {["Delivery", "Pickup"].map((opt) => (
                      <Form.Check
                        key={opt}
                        type="radio"
                        id={`add-bed-method-${opt}`}
                        name="add-bed-delivery-method"
                        label={opt}
                        checked={addServiceForm.bed_delivery_method === opt}
                        onChange={() => setAddServiceForm((p) => ({ ...p, bed_delivery_method: opt }))}
                      />
                    ))}
                  </div>
                </Form.Group>
              </>
            )}

            {addServiceIsBeds && (
              <>
                <div className="fw-semibold mb-2">
                  <i className="bi bi-house me-2" />Bed Details
                </div>
                <table className="mb-3" style={{ borderSpacing: "0 6px", borderCollapse: "separate" }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 90, paddingRight: 16, fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}></th>
                      <th className="text-center" style={{ paddingRight: 12, fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}>Twin</th>
                      <th className="text-center" style={{ fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}>Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BED_COMPONENTS.map(({ label, twinField, fullField }) => (
                      <tr key={label}>
                        <td className="align-middle fw-semibold" style={{ paddingRight: 16, fontSize: "0.9em" }}>{label}</td>
                        <td style={{ paddingRight: 12 }}>
                          <Form.Select
                            value={addServiceForm[twinField]}
                            onChange={(e) => setAddServiceForm((p) => ({ ...p, [twinField]: Number(e.target.value) }))}
                            style={{ width: "80px" }}
                          >
                            <option value={0}>—</option>
                            {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            value={addServiceForm[fullField]}
                            onChange={(e) => setAddServiceForm((p) => ({ ...p, [fullField]: Number(e.target.value) }))}
                            style={{ width: "80px" }}
                          >
                            <option value={0}>—</option>
                            {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </Form.Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {addShowsTransport && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-truck me-2" />Transportation Details
                </div>

                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={addServiceForm.transport_pickup_date}
                        onChange={(e) => setAddServiceForm((p) => ({
                          ...p,
                          transport_pickup_date: e.target.value,
                          service_date: p.service_date || e.target.value,
                        }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Time <span className="text-danger">*</span></Form.Label>
                      <TimePicker
                        value={addServiceForm.transport_pickup_time}
                        onChange={(v) => setAddServiceForm((p) => ({ ...p, transport_pickup_time: v }))}
                        minuteStep={5}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Appointment Location (Destination)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Clinic / facility name and address"
                    value={addServiceForm.transport_dropoff_location_text}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, transport_dropoff_location_text: e.target.value }))}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Pickup Location</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Client's address"
                    value={addServiceForm.transport_pickup_location_text}
                    onChange={(e) => setAddServiceForm((p) => ({ ...p, transport_pickup_location_text: e.target.value }))}
                  />
                </Form.Group>

                <Form.Group className="mb-1">
                  <Form.Label>Provider</Form.Label>
                  <ProviderTypeahead
                    providers={transportProviders}
                    value={addServiceForm.transport_provider_id}
                    onChange={(id) => setAddServiceForm((p) => ({ ...p, transport_provider_id: id }))}
                    disabled={transportLookupLoading}
                  />
                </Form.Group>

                <div className="mb-3">
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => { setShowAddTransportProvider((v) => !v); setTransportProviderError(""); }}
                  >
                    {showAddTransportProvider ? "— Cancel new provider" : "+ Add New Provider"}
                  </Button>
                </div>

                {showAddTransportProvider && (
                  <Card className="mb-3 border-secondary">
                    <Card.Header className="py-1 px-3" style={{ fontSize: "0.85rem", background: "#f8f9fa" }}>
                      New Provider
                    </Card.Header>
                    <Card.Body className="py-2 px-3">
                      {transportProviderError && (
                        <Alert variant="danger" className="py-1 mb-2">{transportProviderError}</Alert>
                      )}
                      <Row className="g-2 align-items-end">
                        <Col md={5}>
                          <Form.Control size="sm" placeholder="Name *"
                            value={newTransportProvider.name}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, name: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && handleAddTransportProvider()}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control size="sm" type="tel" placeholder="Phone"
                            value={newTransportProvider.phone}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, phone: e.target.value }))}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control size="sm" placeholder="City"
                            value={newTransportProvider.city}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, city: e.target.value }))}
                          />
                        </Col>
                        <Col md={2}>
                          <Button size="sm" variant="success" className="w-100"
                            onClick={handleAddTransportProvider}
                            disabled={addingTransportProvider}
                          >
                            {addingTransportProvider ? <Spinner size="sm" animation="border" /> : "Save"}
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Rider Category <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={addServiceForm.transport_rider_category}
                        onChange={(e) => setAddServiceForm((p) => ({ ...p, transport_rider_category: e.target.value }))}
                      >
                        <option value="">— Select —</option>
                        <option value="MAN">MAN</option>
                        <option value="WOMAN">WOMAN</option>
                        <option value="COUPLE">COUPLE</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Used to match eligible drivers.</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Alert variant="info" className="py-2 mb-0">
                  {addServiceIsTransport && "This request auto-approves on save. "}
                  Assign a driver and manage the ride from the Transportation Dashboard.
                </Alert>
              </>
            )}

            {!addServiceIsTransport && (
              <Form.Group className="mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select
                  value={addServiceForm.status || "Open"}
                  onChange={(e) => setAddServiceForm((p) => ({ ...p, status: e.target.value }))}
                >
                  {statusOptionsFor(addServiceIsTransport, addServiceForm.status || "Open").map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {(addServiceNeedsDelivery || addServiceIsPersonalBag) && addServiceForm.status === "Approved" && (
              <Form.Group className="mb-3">
                <Form.Label>{addServiceIsPersonalBag ? "Pickup Date" : "Pickup / Delivery Date"}</Form.Label>
                <Form.Control
                  type="date"
                  value={addServiceForm.pickup_delivery_date || ""}
                  onChange={(e) => setAddServiceForm((p) => ({ ...p, pickup_delivery_date: e.target.value }))}
                />
              </Form.Group>
            )}

            {/* Transportation requests are coordinated on the Transportation
                Dashboard, so they aren't assigned to a staff member here. */}
            {!addServiceIsTransport && (
              <Form.Group className="mb-3">
                <Form.Label>Assigned To</Form.Label>
                <Form.Select
                  value={addServiceForm.assigned_to ?? ""}
                  onChange={(e) => setAddServiceForm((p) => ({ ...p, assigned_to: e.target.value }))}
                  disabled={assignableUsers.length === 0}
                >
                  <option value="">— Unassigned —</option>
                  {(assignableUsers || []).map((u) => {
                    const label = u?.full_name || u?.email || `User #${u.id}`;
                    return (
                      <option key={u.id} value={u.id}>
                        {label}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            )}

            {(addServiceForm.status === "Approved" || addServiceForm.status === "Denied") && (
              <Form.Group className="mb-3">
                <Form.Label>Decision Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={addServiceForm.notes || ""}
                  onChange={(e) => setAddServiceForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Initial Internal Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={addServiceForm.internal_notes || ""}
                onChange={(e) => setAddServiceForm((p) => ({ ...p, internal_notes: e.target.value }))}
                placeholder="Optional — you can add more notes after saving."
              />
            </Form.Group>

            {/* Help already received — collapsible */}
            <div className="mb-3">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setShowHelpSection((v) => !v)}
              >
                <Form.Label className="mb-0">What help have you already received or applied for?</Form.Label>
                <i className={`bi bi-chevron-${showHelpSection ? "up" : "down"} ms-2`} />
              </div>
              {showHelpSection && (
                <div className="d-flex flex-wrap gap-3 mt-2">
                  {HELP_ALREADY_RECEIVED_OPTIONS.map((opt) => {
                    const checked = (addServiceForm.help_already_received || []).includes(opt);
                    return (
                      <Form.Check
                        key={opt}
                        type="checkbox"
                        id={`add-help-${opt}`}
                        label={opt}
                        checked={checked}
                        onChange={(e) => {
                          setAddServiceForm((p) => {
                            const prev = Array.isArray(p.help_already_received) ? p.help_already_received : [];
                            if (e.target.checked) return { ...p, help_already_received: [...new Set([...prev, opt])] };
                            return { ...p, help_already_received: prev.filter((x) => x !== opt) };
                          });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Family help — collapsible */}
            <div className="mb-2">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setShowFamilySection((v) => !v)}
              >
                <Form.Label className="mb-0">Has your family been able to help you?</Form.Label>
                <i className={`bi bi-chevron-${showFamilySection ? "up" : "down"} ms-2`} />
              </div>
              {showFamilySection && (
                <>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      name="add-family-helped"
                      id="add-family-helped-yes"
                      label="Yes"
                      checked={addServiceForm.family_helped === "Yes"}
                      onChange={() => setAddServiceForm((p) => ({ ...p, family_helped: "Yes" }))}
                    />
                    <Form.Check
                      type="radio"
                      name="add-family-helped"
                      id="add-family-helped-no"
                      label="No"
                      checked={addServiceForm.family_helped !== "Yes"}
                      onChange={() =>
                        setAddServiceForm((p) => ({ ...p, family_helped: "No", family_help_offered: "" }))
                      }
                    />
                  </div>
                  {addServiceForm.family_helped === "Yes" && (
                    <Form.Group className="mt-2">
                      <Form.Label>If Yes, what help have they been able to offer?</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={addServiceForm.family_help_offered || ""}
                        onChange={(e) =>
                          setAddServiceForm((p) => ({ ...p, family_help_offered: e.target.value }))
                        }
                      />
                    </Form.Group>
                  )}
                </>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowAddService(false)} disabled={savingService}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddServiceSave} disabled={savingService}>
            {savingService ? "Saving..." : addServiceForm.service_type_ids.length > 1 ? "Save & Next" : "Save Service"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------- Transportation Modal ---------- */}
      <TransportationModal
        show={showTransportModal}
        onHide={() => {
          setShowTransportModal(false);
          setEditingRide(null);
        }}
        onSaved={() => {
          setShowTransportModal(false);
          setEditingRide(null);
          loadRides();
        }}
        clientId={Number(id)}
        clientData={client}
        editRide={editingRide}
        context="service-request"
      />

      {/* ---------- Emergency Contact Required (transportation) ---------- */}
      <Modal
        show={showEmergencyModal}
        onHide={() => setShowEmergencyModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Emergency Contact Needed</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {emergencyError && <Alert variant="danger" className="py-2">{emergencyError}</Alert>}
          <p className="text-muted">
            This client has no emergency contact on file. One is strongly
            recommended for transportation &mdash; it will be shown to the
            coordinator and the driver. You can add it now, or choose
            &ldquo;Skip for now&rdquo; to continue (you&rsquo;ll be reminded again
            next time this client&rsquo;s transportation is added or edited).
          </p>
          <Form.Group className="mb-3">
            <Form.Label>Emergency Contact Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              autoFocus
              value={emergencyForm.name}
              onChange={(e) => setEmergencyForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Jane Smith (daughter)"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Emergency Contact Phone <span className="text-danger">*</span></Form.Label>
            <Form.Control
              value={emergencyForm.phone}
              onChange={(e) => setEmergencyForm((p) => ({ ...p, phone: e.target.value }))}
              placeholder="989-555-1234"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={skipEmergencyContact} disabled={emergencySaving}>
            Skip for now
          </Button>
          <Button variant="primary" onClick={saveEmergencyContact} disabled={emergencySaving}>
            {emergencySaving && <Spinner size="sm" animation="border" className="me-1" />}
            Save &amp; Continue
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ---------- Edit Service Modal ---------- */}
      <Modal show={showEditService} onHide={closeEditService} centered size="lg">
        <Modal.Header className="d-block">
          {clientInfoLine}
          <div className="d-flex align-items-center">
            <Modal.Title className="h5 mb-0">Edit Service</Modal.Title>
            <div className="ms-auto d-flex align-items-center gap-2">
              {(user?.roles?.includes("ADMIN") || user?.roles?.includes("DIRECTOR")) && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddTaskFromEdit}
                  disabled={savingService}
                >
                  <i className="bi bi-plus-circle me-1" />Add Task
                </Button>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={handleEditServiceSave}
                disabled={savingService}
              >
                {savingService ? "Saving..." : "Save"}
              </Button>
              <CloseButton aria-label="Close" onClick={() => setCloseGuard("edit")} />
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          {lookupWarning ? <Alert variant="warning">{lookupWarning}</Alert> : null}
          {serviceFormError ? <Alert variant="danger">{serviceFormError}</Alert> : null}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Service Date</Form.Label>
              <Form.Control
                type="date"
                value={editServiceForm.service_date || ""}
                onChange={(e) => setEditServiceForm((p) => ({ ...p, service_date: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Service Types</Form.Label>
              <div className="d-flex flex-wrap gap-1 pt-1">
                {(editServiceForm.service_type_ids || []).map((tid) => {
                  const st = serviceTypeById.get(Number(tid));
                  return (
                    <Badge key={tid} bg="secondary" style={{ fontSize: "0.9em" }}>
                      {st?.name || `Type #${tid}`}
                    </Badge>
                  );
                })}
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={editServiceForm.status || "Open"}
                onChange={(e) => setEditServiceForm((p) => ({ ...p, status: e.target.value }))}
              >
                {statusOptionsFor(editServiceIsTransport, editServiceForm.status || "Open").map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Form.Select>
              {editServiceIsTransport && (
                <Form.Text className="text-muted">
                  Confirmed, Assigned and Complete are set on the Transportation Dashboard.
                </Form.Text>
              )}
            </Form.Group>

            {(editServiceNeedsDelivery || editServiceIsPersonalBag) && editServiceForm.status === "Approved" && (
              <Form.Group className="mb-3">
                <Form.Label>{editServiceIsPersonalBag ? "Pickup Date" : "Pickup / Delivery Date"}</Form.Label>
                <Form.Control
                  type="date"
                  value={editServiceForm.pickup_delivery_date || ""}
                  onChange={(e) => setEditServiceForm((p) => ({ ...p, pickup_delivery_date: e.target.value }))}
                />
              </Form.Group>
            )}

            {/* Transportation requests are coordinated on the Transportation
                Dashboard, so they aren't assigned to a staff member here. */}
            {!editServiceIsTransport && (
              <Form.Group className="mb-3">
                <Form.Label>Assigned To</Form.Label>
                <Form.Select
                  value={editServiceForm.assigned_to ?? ""}
                  onChange={(e) => setEditServiceForm((p) => ({ ...p, assigned_to: e.target.value }))}
                  disabled={assignableUsers.length === 0}
                >
                  <option value="">— Unassigned —</option>
                  {(assignableUsers || []).map((u) => {
                    const label = u?.full_name || u?.email || `User #${u.id}`;
                    return (
                      <option key={u.id} value={u.id}>
                        {label}
                      </option>
                    );
                  })}
                </Form.Select>
              </Form.Group>
            )}

            {(editServiceForm.status === "Approved" || editServiceForm.status === "Denied") && (
              <Form.Group className="mb-3">
                <Form.Label>Decision Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editServiceForm.notes || ""}
                  onChange={(e) => setEditServiceForm((p) => ({ ...p, notes: e.target.value }))}
                />
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">Internal Notes</Form.Label>
                {!addingNote && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => { setAddingNote(true); setNewNoteText(""); setNoteError(""); }}
                    disabled={noteBusy}
                  >
                    <i className="bi bi-plus-circle me-1" />Add Note
                  </Button>
                )}
              </div>

              {noteError ? <Alert variant="danger" className="py-2">{noteError}</Alert> : null}

              {addingNote && (
                <div className="mb-3 p-2 border rounded bg-light">
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={newNoteText}
                    onChange={(e) => setNewNoteText(e.target.value)}
                    placeholder="Type a new internal note…"
                    autoFocus
                  />
                  <div className="d-flex gap-2 mt-2">
                    <Button size="sm" variant="primary" onClick={handleAddNote} disabled={noteBusy || !newNoteText.trim()}>
                      {noteBusy ? <Spinner animation="border" size="sm" /> : "Save Note"}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => { setAddingNote(false); setNewNoteText(""); }} disabled={noteBusy}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {editServiceNotes.length === 0 && !addingNote && (
                <div className="text-muted fst-italic" style={{ fontSize: "0.9rem" }}>
                  No internal notes yet.
                </div>
              )}

              <ListGroup variant="flush">
                {[...editServiceNotes].sort((a, b) => b.id - a.id).map((note) => (
                  <ListGroup.Item key={note.id} className="px-0 py-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <small className="text-muted">
                        {formatNoteTime(note.updated_at || note.created_at)}
                        {note.created_by_name ? ` · ${note.created_by_name}` : ""}
                      </small>
                      {canEditNote(user, note) && editingNoteId !== note.id && (
                        <div className="d-flex gap-2 ms-2">
                          <Button variant="link" size="sm" className="p-0 text-decoration-none"
                            onClick={() => startEditNote(note)} disabled={noteBusy}>
                            Edit
                          </Button>
                          <Button variant="link" size="sm" className="p-0 text-decoration-none text-danger"
                            onClick={() => handleDeleteNote(note.id)} disabled={noteBusy}>
                            Delete
                          </Button>
                        </div>
                      )}
                    </div>

                    {editingNoteId === note.id ? (
                      <div className="mt-1">
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={editingNoteText}
                          onChange={(e) => setEditingNoteText(e.target.value)}
                          autoFocus
                        />
                        <div className="d-flex gap-2 mt-2">
                          <Button size="sm" variant="primary" onClick={() => handleSaveEditNote(note.id)} disabled={noteBusy || !editingNoteText.trim()}>
                            {noteBusy ? <Spinner animation="border" size="sm" /> : "Save"}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={cancelEditNote} disabled={noteBusy}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ whiteSpace: "pre-wrap" }}>{note.note}</div>
                    )}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Form.Group>

            {/* Help already received — collapsible */}
            <div className="mb-3">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setShowHelpSection((v) => !v)}
              >
                <Form.Label className="mb-0">What help have you already received or applied for?</Form.Label>
                <i className={`bi bi-chevron-${showHelpSection ? "up" : "down"} ms-2`} />
              </div>
              {showHelpSection && (
                <div className="d-flex flex-wrap gap-3 mt-2">
                  {HELP_ALREADY_RECEIVED_OPTIONS.map((opt) => {
                    const checked = (editServiceForm.help_already_received || []).includes(opt);
                    return (
                      <Form.Check
                        key={opt}
                        type="checkbox"
                        id={`edit-help-${opt}`}
                        label={opt}
                        checked={checked}
                        onChange={(e) => {
                          setEditServiceForm((p) => {
                            const prev = Array.isArray(p.help_already_received) ? p.help_already_received : [];
                            if (e.target.checked) return { ...p, help_already_received: [...new Set([...prev, opt])] };
                            return { ...p, help_already_received: prev.filter((x) => x !== opt) };
                          });
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>

            {/* Family help — collapsible */}
            <div className="mb-2">
              <div
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setShowFamilySection((v) => !v)}
              >
                <Form.Label className="mb-0">Has your family been able to help you?</Form.Label>
                <i className={`bi bi-chevron-${showFamilySection ? "up" : "down"} ms-2`} />
              </div>
              {showFamilySection && (
                <>
                  <div className="d-flex gap-4 mt-2">
                    <Form.Check
                      type="radio"
                      name="edit-family-helped"
                      id="edit-family-helped-yes"
                      label="Yes"
                      checked={editServiceForm.family_helped === "Yes"}
                      onChange={() => setEditServiceForm((p) => ({ ...p, family_helped: "Yes" }))}
                    />
                    <Form.Check
                      type="radio"
                      name="edit-family-helped"
                      id="edit-family-helped-no"
                      label="No"
                      checked={editServiceForm.family_helped !== "Yes"}
                      onChange={() =>
                        setEditServiceForm((p) => ({ ...p, family_helped: "No", family_help_offered: "" }))
                      }
                    />
                  </div>
                  {editServiceForm.family_helped === "Yes" && (
                    <Form.Group className="mt-2">
                      <Form.Label>If Yes, what help have they been able to offer?</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={editServiceForm.family_help_offered || ""}
                        onChange={(e) =>
                          setEditServiceForm((p) => ({ ...p, family_help_offered: e.target.value }))
                        }
                      />
                    </Form.Group>
                  )}
                </>
              )}
            </div>

            {editServiceIsBusCard && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-ticket-perforated me-2" />Bus Card Details
                </div>
                {client?.uses_transit && (
                  <p className="mb-3 fw-semibold">
                    Transit Fare: ${client.transit_fare}
                  </p>
                )}
                {isAdminOrDirector ? (
                  <Form.Group className="mb-3">
                    <Form.Label># of Cards</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      step="1"
                      value={editServiceForm.tickets_requested || ""}
                      onChange={(e) => setEditServiceForm((p) => ({ ...p, tickets_requested: e.target.value }))}
                    />
                  </Form.Group>
                ) : editIsApproved
                  ? readonlyRow("# of Cards", editServiceForm.tickets_requested)
                  : adminOnlyDetailsNote}
              </>
            )}

            {editServiceIsLaundry && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-basket me-2" />Laundry Details
                </div>
                {isAdminOrDirector ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Type</Form.Label>
                      <div className="d-flex gap-4">
                        {["Card", "Ministry"].map((t) => (
                          <Form.Check
                            key={t}
                            type="radio"
                            id={`edit-laundry-type-${t}`}
                            name="edit-laundry-type"
                            label={t}
                            checked={editServiceForm.laundry_type === t}
                            onChange={() => setEditServiceForm((p) => ({ ...p, laundry_type: t }))}
                          />
                        ))}
                      </div>
                    </Form.Group>
                    {editServiceIsLaundryMinistry && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <div className="d-flex gap-4">
                            {["Clare", "Harrison"].map((loc) => (
                              <Form.Check
                                key={loc}
                                type="radio"
                                id={`edit-laundry-loc-${loc}`}
                                name="edit-laundry-location"
                                label={loc}
                                checked={editServiceForm.service_location === loc}
                                onChange={() => setEditServiceForm((p) => ({ ...p, service_location: loc }))}
                              />
                            ))}
                          </div>
                        </Form.Group>
                        <Form.Check
                          type="checkbox"
                          id="edit-laundry-needs-transport"
                          label="Transportation required"
                          className="mb-3"
                          checked={!!editServiceForm.laundry_needs_transport}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, laundry_needs_transport: e.target.checked }))}
                        />
                      </>
                    )}
                  </>
                ) : editIsApproved ? (
                  <>
                    {readonlyRow("Type", editServiceForm.laundry_type)}
                    {editServiceForm.laundry_type === "Ministry" &&
                      readonlyRow("Location", editServiceForm.service_location)}
                  </>
                ) : adminOnlyDetailsNote}
              </>
            )}

            {editServiceIsFood && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-bag me-2" />Food Details
                </div>
                {isAdminOrDirector ? (
                <>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Card", "Pantry"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`edit-food-type-${t}`}
                        name="edit-food-type"
                        label={t}
                        checked={editServiceForm.food_type === t}
                        onChange={() => setEditServiceForm((p) => ({ ...p, food_type: t }))}
                      />
                    ))}
                  </div>
                </Form.Group>
                {editServiceIsFoodCard && (
                  <Row className="g-2 align-items-end mb-3">
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Value ($)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          style={{ width: "110px" }}
                          value={editServiceForm.food_card_value}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, food_card_value: e.target.value }))}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Qty</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          style={{ width: "80px" }}
                          value={editServiceForm.food_card_qty}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, food_card_qty: e.target.value }))}
                          placeholder="0"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Total</Form.Label>
                        <div className="fw-semibold pt-1">
                          ${((parseFloat(editServiceForm.food_card_value) || 0) * (parseInt(editServiceForm.food_card_qty) || 0)).toFixed(2)}
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                )}
                </>
                ) : editIsApproved ? (
                  <>
                    {readonlyRow("Type", editServiceForm.food_type)}
                    {editServiceForm.food_type === "Card" &&
                      readonlyRow(
                        "Card",
                        `$${(parseFloat(editServiceForm.food_card_value) || 0).toFixed(2)} × ${parseInt(editServiceForm.food_card_qty) || 0} = $${((parseFloat(editServiceForm.food_card_value) || 0) * (parseInt(editServiceForm.food_card_qty) || 0)).toFixed(2)}`
                      )}
                  </>
                ) : adminOnlyDetailsNote}
              </>
            )}

            {editServiceIsGas && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-fuel-pump me-2" />Gas Details
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Card", "Cash"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`edit-gas-type-${t}`}
                        name="edit-gas-type"
                        label={t}
                        // Card is not currently offered — grayed out, Cash only.
                        disabled={t === "Card"}
                        checked={editServiceForm.gas_type === t}
                        onChange={() => setEditServiceForm((p) => ({ ...p, gas_type: t, gas_location_id: "" }))}
                      />
                    ))}
                  </div>
                </Form.Group>
                {isAdminOrDirector ? (
                  <Form.Group className="mb-3">
                    <Form.Label>Amount ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      style={{ width: "130px" }}
                      value={editServiceForm.gas_amount}
                      onChange={(e) => setEditServiceForm((p) => ({ ...p, gas_amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>
                ) : editIsApproved
                  ? readonlyRow("Amount ($)", editServiceForm.gas_amount != null && editServiceForm.gas_amount !== ""
                      ? `$${(parseFloat(editServiceForm.gas_amount) || 0).toFixed(2)}`
                      : "—")
                  : adminOnlyDetailsNote}
                {isAdminOrDirector && (
                  <Form.Group className="mb-3">
                    <Form.Label>Location</Form.Label>
                    <Form.Select
                      style={{ maxWidth: "280px" }}
                      value={editServiceForm.gas_location_id}
                      onChange={(e) => {
                        if (e.target.value === "__new__") {
                          setShowAddGasLocation(true);
                          setNewGasLocationName("");
                        } else {
                          setEditServiceForm((p) => ({ ...p, gas_location_id: e.target.value }));
                          setShowAddGasLocation(false);
                        }
                      }}
                    >
                      <option value="">— Select location —</option>
                      {gasLocations.map((loc) => (
                        <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                      ))}
                      <option value="__new__">+ Add new location…</option>
                    </Form.Select>
                    {showAddGasLocation && (
                      <div className="d-flex gap-2 mt-2">
                        <Form.Control
                          type="text" placeholder="New location name" style={{ maxWidth: "220px" }}
                          value={newGasLocationName}
                          onChange={(e) => setNewGasLocationName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveGasLocation(setEditServiceForm); } }}
                        />
                        <Button variant="outline-primary" size="sm"
                          disabled={savingGasLocation || !newGasLocationName.trim()}
                          onClick={() => handleSaveGasLocation(setEditServiceForm)}>
                          {savingGasLocation ? "Saving…" : "Add"}
                        </Button>
                        <Button variant="outline-secondary" size="sm"
                          onClick={() => { setShowAddGasLocation(false); setNewGasLocationName(""); }}>
                          Cancel
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                )}
                <Form.Group className="mb-3">
                  <Form.Label>Appointment Date</Form.Label>
                  <Form.Control
                    type="date"
                    style={{ width: "180px" }}
                    value={editServiceForm.gas_appt_date}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, gas_appt_date: e.target.value }))}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Destination</Form.Label>
                  <Form.Control
                    type="text"
                    value={editServiceForm.gas_destination}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, gas_destination: e.target.value }))}
                    placeholder="Where the client is traveling to"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Reason</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editServiceForm.gas_appointment_reason}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, gas_appointment_reason: e.target.value }))}
                    placeholder="Appointment the gas is for"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="edit-gas-appointment-verified"
                    label="Verified"
                    checked={editServiceForm.gas_appointment_verified}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, gas_appointment_verified: e.target.checked }))}
                  />
                </Form.Group>
              </>
            )}

            {editServiceIsPropane && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-fire me-2" />Propane Details
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Type</Form.Label>
                  <div className="d-flex gap-4">
                    {["Pig", "Tank"].map((t) => (
                      <Form.Check
                        key={t}
                        type="radio"
                        id={`edit-propane-type-${t}`}
                        name="edit-propane-type"
                        label={t}
                        checked={editServiceForm.propane_type === t}
                        onChange={() => setEditServiceForm((p) => ({ ...p, propane_type: t, propane_location_id: "" }))}
                      />
                    ))}
                  </div>
                </Form.Group>

                {editServiceForm.propane_type === "Pig" && (
                  <Row className="g-2 align-items-end mb-3">
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Fill Amount (gal)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.001" style={{ width: "130px" }}
                          value={editServiceForm.propane_gallons}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_gallons: e.target.value }))}
                          placeholder="0.000"
                        />
                      </Form.Group>
                    </Col>
                    <Col xs="auto">
                      <Form.Group>
                        <Form.Label>Fill Price ($)</Form.Label>
                        <Form.Control
                          type="number" min="0" step="0.01" style={{ width: "120px" }}
                          value={editServiceForm.propane_fill_price}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_fill_price: e.target.value }))}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                {editServiceIsPropaneTank && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Service</Form.Label>
                      <div className="d-flex gap-4">
                        {["Refill", "Exchange"].map((opt) => (
                          <Form.Check
                            key={opt}
                            type="radio"
                            id={`edit-propane-tank-svc-${opt}`}
                            name="edit-propane-tank-service"
                            label={opt}
                            checked={editServiceForm.propane_tank_service === opt}
                            onChange={() => setEditServiceForm((p) => ({
                              ...p,
                              propane_tank_service: opt,
                              propane_location_id: "",
                              propane_tank_count_100: "",
                              propane_cost_per_100: "",
                            }))}
                          />
                        ))}
                      </div>
                    </Form.Group>

                    {editServiceForm.propane_tank_service && (
                      <>
                        <table className="mb-3" style={{ borderSpacing: "0 6px", borderCollapse: "separate" }}>
                          <thead>
                            <tr>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em", minWidth: 48 }}>Size</th>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Qty</th>
                              <th className="pe-3 text-muted fw-normal" style={{ fontSize: "0.85em" }}>Cost Per ($)</th>
                              <th className="text-muted fw-normal" style={{ fontSize: "0.85em" }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="pe-3 align-middle fw-semibold">20#</td>
                              <td className="pe-3">
                                <Form.Control
                                  type="number" min="0" step="1" style={{ width: "80px" }}
                                  value={editServiceForm.propane_tank_count}
                                  onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_tank_count: e.target.value }))}
                                  placeholder="0"
                                />
                              </td>
                              <td className="pe-3">
                                <Form.Control
                                  type="number" min="0" step="0.01" style={{ width: "100px" }}
                                  value={editServiceForm.propane_cost_per}
                                  onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_cost_per: e.target.value }))}
                                  placeholder="0.00"
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="text" readOnly style={{ width: "100px" }}
                                  value={
                                    editServiceForm.propane_tank_count && editServiceForm.propane_cost_per
                                      ? `$${(parseFloat(editServiceForm.propane_tank_count) * parseFloat(editServiceForm.propane_cost_per)).toFixed(2)}`
                                      : "—"
                                  }
                                />
                              </td>
                            </tr>
                            {editServiceForm.propane_tank_service === "Refill" && (
                              <tr>
                                <td className="pe-3 align-middle fw-semibold">100#</td>
                                <td className="pe-3">
                                  <Form.Control
                                    type="number" min="0" step="1" style={{ width: "80px" }}
                                    value={editServiceForm.propane_tank_count_100}
                                    onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_tank_count_100: e.target.value }))}
                                    placeholder="0"
                                  />
                                </td>
                                <td className="pe-3">
                                  <Form.Control
                                    type="number" min="0" step="0.01" style={{ width: "100px" }}
                                    value={editServiceForm.propane_cost_per_100}
                                    onChange={(e) => setEditServiceForm((p) => ({ ...p, propane_cost_per_100: e.target.value }))}
                                    placeholder="0.00"
                                  />
                                </td>
                                <td>
                                  <Form.Control
                                    type="text" readOnly style={{ width: "100px" }}
                                    value={
                                      editServiceForm.propane_tank_count_100 && editServiceForm.propane_cost_per_100
                                        ? `$${(parseFloat(editServiceForm.propane_tank_count_100) * parseFloat(editServiceForm.propane_cost_per_100)).toFixed(2)}`
                                        : "—"
                                    }
                                  />
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <Form.Group className="mb-3">
                          <Form.Label>Location</Form.Label>
                          <Form.Select
                            style={{ maxWidth: "280px" }}
                            value={editServiceForm.propane_location_id}
                            onChange={(e) => {
                              if (e.target.value === "__new__") {
                                setShowAddLpLocation(true);
                                setNewLpLocationName("");
                              } else {
                                setEditServiceForm((p) => ({ ...p, propane_location_id: e.target.value }));
                                setShowAddLpLocation(false);
                              }
                            }}
                          >
                            <option value="">— Select location —</option>
                            {lpLocations
                              .filter((loc) => !loc.type || loc.type === editServiceForm.propane_tank_service.toLowerCase())
                              .map((loc) => (
                                <option key={loc.id} value={String(loc.id)}>{loc.name}</option>
                              ))}
                            <option value="__new__">+ Add new location…</option>
                          </Form.Select>
                          {showAddLpLocation && (
                            <div className="d-flex gap-2 mt-2">
                              <Form.Control
                                type="text" placeholder="New location name" style={{ maxWidth: "220px" }}
                                value={newLpLocationName}
                                onChange={(e) => setNewLpLocationName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveLpLocation(setEditServiceForm, editServiceForm.propane_tank_service); } }}
                              />
                              <Button variant="outline-primary" size="sm"
                                disabled={savingLpLocation || !newLpLocationName.trim()}
                                onClick={() => handleSaveLpLocation(setEditServiceForm, editServiceForm.propane_tank_service)}>
                                {savingLpLocation ? "Saving…" : "Add"}
                              </Button>
                              <Button variant="outline-secondary" size="sm"
                                onClick={() => { setShowAddLpLocation(false); setNewLpLocationName(""); }}>
                                Cancel
                              </Button>
                            </div>
                          )}
                        </Form.Group>
                      </>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="edit-propane-delivery"
                        label={<span className="text-muted">Delivery <small>(coming soon)</small></span>}
                        disabled
                      />
                    </Form.Group>
                  </>
                )}
              </>
            )}

            {editServiceIsBillPay && (() => {
              const { stillNeeded, clientOwes } = calcBillPay(editServiceForm);
              return (
                <>
                  <hr className="my-3" />
                  <div className="fw-semibold mb-3">
                    <i className="bi bi-receipt me-2" />Help Pay a Bill Details
                  </div>

                  <Row className="g-2 mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Bill Type</Form.Label>
                        <Form.Control
                          list="edit-bill-type-options"
                          value={editServiceForm.bill_type || ""}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, bill_type: e.target.value }))}
                          placeholder="Select or type bill type…"
                        />
                        <datalist id="edit-bill-type-options">
                          {BILL_PAY_DEFAULT_TYPES.map((t) => <option key={t} value={t} />)}
                        </datalist>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Account Number</Form.Label>
                        <Form.Control
                          type="text"
                          value={editServiceForm.bill_account_number || ""}
                          onChange={(e) => setEditServiceForm((p) => ({ ...p, bill_account_number: e.target.value }))}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Amount Due</Form.Label>
                    <Form.Control
                      type="number" min="0" step="0.01" style={{ maxWidth: "160px" }}
                      value={editServiceForm.bill_amount_due || ""}
                      onChange={(e) => setEditServiceForm((p) => ({ ...p, bill_amount_due: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Client Has</Form.Label>
                    <Form.Control
                      type="number" min="0" step="0.01" style={{ maxWidth: "160px" }}
                      value={editServiceForm.bill_client_has || ""}
                      onChange={(e) => setEditServiceForm((p) => ({ ...p, bill_client_has: e.target.value }))}
                      placeholder="0.00"
                    />
                  </Form.Group>

                  <div className="fw-semibold mb-2" style={{ fontSize: "0.9em" }}>Agency Contributions</div>
                  <table className="mb-3 w-100" style={{ maxWidth: "400px" }}>
                    <tbody>
                      {BILL_PAY_AGENCIES.map(({ key, label }) => (
                        <tr key={key}>
                          <td className="pe-3 py-1 align-middle" style={{ width: "160px" }}>{label}</td>
                          <td className="py-1">
                            <Form.Control
                              type="number" min="0" step="0.01" style={{ width: "120px" }}
                              value={editServiceForm[key] || ""}
                              onChange={(e) => setEditServiceForm((p) => ({ ...p, [key]: e.target.value }))}
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <Row className="g-2 mb-3 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <span className="fw-semibold">Amount Still Needed</span>
                    </Col>
                    <Col xs="auto">
                      <span className={`fw-semibold ${stillNeeded < 0 ? "text-danger" : ""}`}>
                        ${stillNeeded.toFixed(2)}
                      </span>
                    </Col>
                  </Row>

                  <Row className="g-2 mb-3 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <Form.Label className="mb-0">HHH Amount</Form.Label>
                    </Col>
                    <Col xs="auto">
                      <Form.Control
                        type="number" min="0" step="0.01" style={{ width: "120px" }}
                        value={editServiceForm.bill_hhh_amount || ""}
                        onChange={(e) => setEditServiceForm((p) => ({ ...p, bill_hhh_amount: e.target.value }))}
                        placeholder="0.00"
                      />
                    </Col>
                  </Row>

                  <Row className="g-2 mb-2 align-items-center">
                    <Col xs="auto" style={{ width: "160px" }}>
                      <span className="fw-semibold">Client Must Provide</span>
                    </Col>
                    <Col xs="auto">
                      <span className={`fw-semibold ${clientOwes < 0 ? "text-danger" : "text-primary"}`}>
                        ${clientOwes.toFixed(2)}
                      </span>
                    </Col>
                  </Row>
                </>
              );
            })()}

            {editServiceNeedsDelivery && (
              <>
                <hr className="my-3" />
                <Form.Group className="mb-3">
                  <Form.Label>Delivery Method</Form.Label>
                  <div className="d-flex gap-4 mt-1">
                    {["Delivery", "Pickup"].map((opt) => (
                      <Form.Check
                        key={opt}
                        type="radio"
                        id={`edit-bed-method-${opt}`}
                        name="edit-bed-delivery-method"
                        label={opt}
                        checked={editServiceForm.bed_delivery_method === opt}
                        onChange={() => setEditServiceForm((p) => ({ ...p, bed_delivery_method: opt }))}
                      />
                    ))}
                  </div>
                </Form.Group>
              </>
            )}

            {editServiceIsBeds && (
              <>
                <div className="fw-semibold mb-2">
                  <i className="bi bi-house me-2" />Bed Details
                </div>
                <table className="mb-3" style={{ borderSpacing: "0 6px", borderCollapse: "separate" }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 90, paddingRight: 16, fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}></th>
                      <th className="text-center" style={{ paddingRight: 12, fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}>Twin</th>
                      <th className="text-center" style={{ fontSize: "0.85em", fontWeight: "normal", color: "#6c757d" }}>Full</th>
                    </tr>
                  </thead>
                  <tbody>
                    {BED_COMPONENTS.map(({ label, twinField, fullField }) => (
                      <tr key={label}>
                        <td className="align-middle fw-semibold" style={{ paddingRight: 16, fontSize: "0.9em" }}>{label}</td>
                        <td style={{ paddingRight: 12 }}>
                          <Form.Select
                            value={editServiceForm[twinField]}
                            onChange={(e) => setEditServiceForm((p) => ({ ...p, [twinField]: Number(e.target.value) }))}
                            style={{ width: "80px" }}
                          >
                            <option value={0}>—</option>
                            {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            value={editServiceForm[fullField]}
                            onChange={(e) => setEditServiceForm((p) => ({ ...p, [fullField]: Number(e.target.value) }))}
                            style={{ width: "80px" }}
                          >
                            <option value={0}>—</option>
                            {[1,2,3,4,5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </Form.Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {editShowsTransport && (
              <>
                <hr className="my-3" />
                <div className="fw-semibold mb-3">
                  <i className="bi bi-truck me-2" />Transportation Details
                </div>

                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Date <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        type="date"
                        value={editServiceForm.transport_pickup_date}
                        onChange={(e) => setEditServiceForm((p) => ({
                          ...p,
                          transport_pickup_date: e.target.value,
                          service_date: p.service_date || e.target.value,
                        }))}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Appointment Time <span className="text-danger">*</span></Form.Label>
                      <TimePicker
                        value={editServiceForm.transport_pickup_time}
                        onChange={(v) => setEditServiceForm((p) => ({ ...p, transport_pickup_time: v }))}
                        minuteStep={5}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Appointment Location (Destination)</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Clinic / facility name and address"
                    value={editServiceForm.transport_dropoff_location_text}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, transport_dropoff_location_text: e.target.value }))}
                  />
                </Form.Group>

                <Form.Group className="mb-2">
                  <Form.Label>Pickup Location</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Client's address"
                    value={editServiceForm.transport_pickup_location_text}
                    onChange={(e) => setEditServiceForm((p) => ({ ...p, transport_pickup_location_text: e.target.value }))}
                  />
                </Form.Group>

                <Form.Group className="mb-1">
                  <Form.Label>Provider</Form.Label>
                  <ProviderTypeahead
                    providers={transportProviders}
                    value={editServiceForm.transport_provider_id}
                    onChange={(id) => setEditServiceForm((p) => ({ ...p, transport_provider_id: id }))}
                    disabled={transportLookupLoading}
                  />
                </Form.Group>

                <div className="mb-3">
                  <Button
                    size="sm"
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => { setShowAddTransportProvider((v) => !v); setTransportProviderError(""); }}
                  >
                    {showAddTransportProvider ? "— Cancel new provider" : "+ Add New Provider"}
                  </Button>
                </div>

                {showAddTransportProvider && (
                  <Card className="mb-3 border-secondary">
                    <Card.Header className="py-1 px-3" style={{ fontSize: "0.85rem", background: "#f8f9fa" }}>
                      New Provider
                    </Card.Header>
                    <Card.Body className="py-2 px-3">
                      {transportProviderError && (
                        <Alert variant="danger" className="py-1 mb-2">{transportProviderError}</Alert>
                      )}
                      <Row className="g-2 align-items-end">
                        <Col md={5}>
                          <Form.Control size="sm" placeholder="Name *"
                            value={newTransportProvider.name}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, name: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && handleAddTransportProvider()}
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Control size="sm" type="tel" placeholder="Phone"
                            value={newTransportProvider.phone}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, phone: e.target.value }))}
                          />
                        </Col>
                        <Col md={2}>
                          <Form.Control size="sm" placeholder="City"
                            value={newTransportProvider.city}
                            onChange={(e) => setNewTransportProvider((p) => ({ ...p, city: e.target.value }))}
                          />
                        </Col>
                        <Col md={2}>
                          <Button size="sm" variant="success" className="w-100"
                            onClick={handleAddTransportProvider}
                            disabled={addingTransportProvider}
                          >
                            {addingTransportProvider ? <Spinner size="sm" animation="border" /> : "Save"}
                          </Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                )}

                <Row className="g-2 mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Rider Category <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={editServiceForm.transport_rider_category}
                        onChange={(e) => setEditServiceForm((p) => ({ ...p, transport_rider_category: e.target.value }))}
                      >
                        <option value="">— Select —</option>
                        <option value="MAN">MAN</option>
                        <option value="WOMAN">WOMAN</option>
                        <option value="COUPLE">COUPLE</option>
                      </Form.Select>
                      <Form.Text className="text-muted">Used to match eligible drivers.</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Assigned Driver</Form.Label>
                      <div className="form-control-plaintext">
                        {(() => {
                          const d = transportDrivers.find(
                            (x) => String(x.id) === String(editServiceForm.transport_driver_id)
                          );
                          return d ? `${d.display_name} (${d.driver_type})` : "— Unassigned —";
                        })()}
                      </div>
                      <Form.Text className="text-muted">
                        Assigned on the Transportation Dashboard.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-2 mb-3">
                  <Col>
                    <Form.Check
                      type="checkbox"
                      id="edit-appt-confirmed"
                      label="Appointment Confirmed"
                      checked={editServiceForm.transport_appointment_confirmed}
                      onChange={(e) =>
                        setEditServiceForm((p) => ({
                          ...p,
                          transport_appointment_confirmed: e.target.checked,
                        }))
                      }
                    />
                  </Col>
                </Row>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {isAdminOrDirector && (
            <Button
              variant="outline-danger"
              className="me-auto"
              onClick={handleDeleteService}
              disabled={savingService}
            >
              <i className="bi bi-trash me-1" />Delete Service
            </Button>
          )}
          <Button variant="outline-secondary" onClick={closeEditService} disabled={savingService}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditServiceSave} disabled={savingService}>
            {savingService ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Close-confirm for the Add/Edit Service "X" button */}
      <NavGuardModal
        show={!!closeGuard}
        saving={savingService}
        error={serviceFormError}
        onSave={guardOnSave}
        onDiscard={guardOnDiscard}
        onCancel={() => setCloseGuard(null)}
        title="Close without saving?"
        body="Would you like to save your changes before closing?"
        saveLabel="Save & Close"
        discardLabel="Close Without Saving"
      />

      {/* ---------- Add Task Modal ---------- */}
      <Modal show={showTaskModal} onHide={() => setShowTaskModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Task</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {taskError && <div className="alert alert-danger py-2">{taskError}</div>}
          <Form.Group className="mb-3">
            <Form.Label>Assign To <span className="text-muted fw-normal">(Intake Worker)</span></Form.Label>
            <Form.Select
              value={taskForm.assigned_to}
              onChange={(e) => setTaskForm((p) => ({ ...p, assigned_to: e.target.value }))}
            >
              <option value="">— Unassigned —</option>
              {intakeWorkers.map((w) => (
                <option key={w.id} value={w.id}>{w.full_name}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Task <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={taskForm.task}
              onChange={(e) => setTaskForm((p) => ({ ...p, task: e.target.value }))}
              placeholder="Describe the task…"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTaskModal(false)} disabled={taskSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveTask} disabled={taskSaving}>
            {taskSaving ? "Saving…" : "Save Task"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
